<template>
  <!-- wrapper report-ds aplica o design system do dashboard -->
  <div class="report-calls report-ds">
    <!-- Cabeçalho de página no padrão do dashboard (.dashboard-page-header): ícone + título + seletor -->
    <div class="report-page-header">
      <div class="report-page-header__icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          ></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      </div>
      <div class="report-page-header__text">
        <h1 class="report-page-header__title">Relatórios</h1>
        <!-- subtítulo de explicação no lugar do seletor (que foi pro card-header) -->
        <p class="report-page-header__subtitle">
          Relatórios detalhados das chamadas e dos usuários da sua conta.
        </p>
      </div>
    </div>
    <div class="card">
      <div class="card-header">
        <!-- seletor de tipo à esquerda; export/tutorial à direita -->
        <div class="d-flex justify-content-between align-items-center w-100">
          <!-- rótulo + seletor agrupados à esquerda -->
          <div class="d-flex align-items-center">
            <!-- rótulo no molde do "Filtros:" do dashboard -->
            <strong class="report-type-label mr-3">Tipo de relatório:</strong>
            <!-- [TEL-1945] seletor de tipo agora é um segmented control (2 botões numa pílula) — mesma lógica reportType -->
            <div
              class="report-type-segmented"
              :class="`report-type-segmented--${reportType}`"
              role="tablist"
            >
              <span class="report-type-segmented__thumb" />
              <button
                type="button"
                class="report-type-segmented__opt"
                :class="{ active: reportType === 'calls' }"
                @click="reportType = 'calls'"
              >
                Chamadas
              </button>
              <button
                type="button"
                class="report-type-segmented__opt"
                :class="{ active: reportType === 'agents' }"
                @click="reportType = 'agents'"
              >
                Usuários
              </button>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <!-- download no padrão neutro de ícone (igual à engrenagem); botão de tutorial removido -->
            <!-- classe report-download-btn p/ casca de pill neutra -->
            <a
              v-show="reportType === 'calls'"
              href="#"
              class="btn btn-outline-secondary btn-sm report-download-btn"
              :class="{ disabled: cdrsPartial }"
              @click.prevent="csvExport"
              :title="cdrsPartial
                ? 'Relatório incompleto: a busca não terminou'
                : 'Exportar para Excel'"
            >
              <img
                src="/static/images/icons/download.png"
                width="16"
                height="16"
                alt="EXCEL"
              />
            </a>
            <!-- [TEL-1945] mesmo botão para o relatório de Usuários. Aqui o arquivo é montado
                 pelo backend (GET /reports/agents/export), que já entrega as colunas pedidas. -->
            <a
              v-show="reportType === 'agents'"
              href="#"
              class="btn btn-outline-secondary btn-sm report-download-btn"
              :class="{ disabled: usersExporting }"
              @click.prevent="exportUsersReport"
              title="Exportar para Excel"
            >
              <img
                src="/static/images/icons/download.png"
                width="16"
                height="16"
                alt="EXCEL"
              />
            </a>
          </div>
        </div>
      </div>
      <div class="card-body">
        <!-- erro só no relatório de Chamadas (barra agora é compartilhada) -->
        <ErrorAlert
          v-if="reportType === 'calls'"
          :error="error" />
        <!-- A busca é paginada: interrompida no meio, o que sobra na tabela é um recorte
             parcial. Sem este aviso ele passa por completo. -->
        <div
          v-if="reportType === 'calls' && cdrsPartial"
          class="alert alert-warning"
          role="alert"
        >
          A busca foi interrompida antes de terminar. Estas
          <strong>{{ items.length }}</strong> chamadas são apenas parte do período —
          o relatório está incompleto e a exportação fica indisponível. Reduza o
          intervalo de datas ou aplique mais filtros e tente de novo.
        </div>
        <!-- barra de filtros compartilhada (Chamadas e Usuários); filtros exclusivos de Chamadas usam v-show -->
        <div class="report-filter-bar d-flex flex-wrap align-items-center">
          <strong class="report-filter-bar__label mr-2 mb-2">Filtros:</strong>

          <!-- Data: filtro de período (presets + personalizado) no molde do dashboard -->
          <b-dropdown
            ref="dateDropdown"
            :text="dateFilterLabel"
            variant="primary"
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
            menu-class="period-menu"
          >
            <b-dropdown-item
              v-for="preset in datePresets"
              :key="preset.name"
              @click="selectDatePreset(preset)"
            >
              {{ preset.text }}
            </b-dropdown-item>
            <b-dropdown-form
              class="period-custom-form"
              @submit.prevent="applyCustomPeriod"
            >
              <button
                type="button"
                class="dropdown-item period-custom-toggle"
                @click="customPeriodOpen = !customPeriodOpen"
              >
                Personalizado
              </button>
              <div v-if="customPeriodOpen" class="period-custom">
                <label class="period-custom-field"
                  >De <input type="date" v-model="customFrom"
                /></label>
                <label class="period-custom-field"
                  >Até <input type="date" v-model="customTo"
                /></label>
                <div v-if="customPeriodError" class="period-custom-error">
                  {{ customPeriodError }}
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="applyCustomPeriod"
                >
                  OK
                </button>
              </div>
            </b-dropdown-form>
          </b-dropdown>

          <!-- Tipo de chamada: select -> b-dropdown (mesmo binding callType) -->
          <!-- [TEL-1945] Tipo de chamada só no relatório de Chamadas (não faz sentido no agregado por usuário) -->
          <b-dropdown
            v-show="reportType === 'calls'"
            :text="callTypeLabel"
            :variant="
              advancedFilters.callType ? 'primary' : 'outline-secondary'
            "
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
          >
            <b-dropdown-item @click="advancedFilters.callType = ''"
              >Ambas</b-dropdown-item
            >
            <b-dropdown-item @click="advancedFilters.callType = 'outbound'"
              >Sainte</b-dropdown-item
            >
            <b-dropdown-item @click="advancedFilters.callType = 'inbound'"
              >Entrante</b-dropdown-item
            >
          </b-dropdown>

          <!-- [TEL-1945] "Ramais" renomeado p/ "Usuários" (filtra por nome ou ramal via checkbox); filtro de nome separado removido -->
          <!-- Usuários: b-dropdown + checkbox-group (mesmas opções/variável advancedFilters.from) -->
          <!-- [TEL-1945] dependência 3 do card: filtro de Usuário só para admin. Não é controle
               de acesso — o backend já escopa perfil USER ao próprio e-mail nas duas rotas; é
               que para um não-admin o filtro só produziria listas vazias. -->
          <b-dropdown
            v-show="isAdmin"
            :text="extensionsLabel"
            :variant="
              advancedFilters.from.length ? 'primary' : 'outline-secondary'
            "
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
            menu-class="report-checkbox-menu"
          >
            <b-dropdown-form style="min-width: 220px;">
              <!-- [TEL-1945] busca de ramais (por nome ou número) acima da lista -->
              <input
                type="text"
                class="form-control form-control-sm mb-2 report-ext-search"
                v-model="extensionSearch"
                placeholder="Buscar usuário ou ramal..."
                @click.stop
              />
              <b-form-checkbox-group
                v-model="advancedFilters.from"
                :options="filteredExtensionOptions"
                stacked
              />
            </b-dropdown-form>
          </b-dropdown>

          <!-- [TEL-1945] Status (só no relatório de Usuários): filtro por checkbox, múltipla escolha -->
          <b-dropdown
            v-show="reportType === 'agents'"
            :text="usersStatusLabel"
            :variant="
              usersStatusFilter.length ? 'primary' : 'outline-secondary'
            "
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
            menu-class="report-checkbox-menu"
          >
            <b-dropdown-form style="min-width: 220px;">
              <b-form-checkbox-group
                v-model="usersStatusFilter"
                :options="usersStatusOptions"
                stacked
              />
            </b-dropdown-form>
          </b-dropdown>

          <!-- Número discado: input -> b-dropdown com mini-form (molde do "Personalizado"); mesma variável -->
          <b-dropdown
            ref="numberDropdown"
            v-show="reportType === 'calls' && !isPhoneNumberHidden"
            :text="numberLabel"
            :variant="advancedFilters.to ? 'primary' : 'outline-secondary'"
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
            menu-class="report-number-menu"
          >
            <b-dropdown-form
              class="number-custom-form"
              @submit.prevent="applyNumberFilter"
            >
              <div class="number-custom">
                <label class="number-custom-field"
                  >Número discado
                  <input
                    type="text"
                    v-model="pendingNumber"
                    placeholder="ex: 48988487457"
                  />
                </label>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="applyNumberFilter"
                >
                  OK
                </button>
              </div>
            </b-dropdown-form>
          </b-dropdown>

          <!-- Causas: MultiSelect -> b-dropdown + checkbox-group (igual "Status" do dashboard); mesmas opções/variável/lógica -->
          <b-dropdown
            v-show="reportType === 'calls'"
            :text="hangupCauseLabel"
            :variant="
              advancedFilters.hangupCause.length
                ? 'primary'
                : 'outline-secondary'
            "
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
            menu-class="report-checkbox-menu"
          >
            <b-dropdown-form style="min-width: 220px;">
              <!-- [TEL-1945] :checked em vez de v-model — com os dois, clicar em "Mostrar mais
                   opções" gravava o sentinela no array (disparando uma busca) antes de o handler
                   limpá-lo (disparando a segunda). O handler é a única porta de escrita. -->
              <b-form-checkbox-group
                :checked="advancedFilters.hangupCause"
                :options="
                  showAllHangupCauses
                    ? hangupCauseAllOptionsArray
                    : hangupCauseMainOptionsArray
                "
                stacked
                @change="handleHangupCauseChange"
              />
            </b-dropdown-form>
          </b-dropdown>

          <!-- Duração: presets + Personalizado (operador menor/maior que X segundos) -->
          <b-dropdown
            ref="durationDropdown"
            v-show="reportType === 'calls'"
            :text="durationLabel"
            :variant="
              advancedFilters.durationRange ? 'primary' : 'outline-secondary'
            "
            size="sm"
            class="report-filter-dropdown mr-2 mb-2"
            menu-class="duration-menu"
          >
            <b-dropdown-item @click="advancedFilters.durationRange = ''"
              >Todas</b-dropdown-item
            >
            <b-dropdown-item @click="advancedFilters.durationRange = '0-60'"
              >Até 1 minuto</b-dropdown-item
            >
            <b-dropdown-item @click="advancedFilters.durationRange = '60-300'"
              >Entre 1 e 5 min</b-dropdown-item
            >
            <b-dropdown-item @click="advancedFilters.durationRange = '300-600'"
              >Entre 5 e 10 min</b-dropdown-item
            >
            <b-dropdown-item
              @click="advancedFilters.durationRange = '600-999999'"
              >Maior que 10 min</b-dropdown-item
            >
            <!-- [TEL-1945] Personalizado: operador (menor/maior que) + segundos -->
            <b-dropdown-form
              class="duration-custom-form"
              @submit.prevent="applyCustomDuration"
            >
              <button
                type="button"
                class="dropdown-item duration-custom-toggle"
                @click="customDurationOpen = !customDurationOpen"
              >
                Personalizado
              </button>
              <div v-if="customDurationOpen" class="duration-custom">
                <div class="duration-op-toggle">
                  <button
                    type="button"
                    class="duration-op-btn"
                    :class="{ active: customDurationOp === 'lt' }"
                    @click="customDurationOp = 'lt'"
                  >
                    menor que
                  </button>
                  <button
                    type="button"
                    class="duration-op-btn"
                    :class="{ active: customDurationOp === 'gt' }"
                    @click="customDurationOp = 'gt'"
                  >
                    maior que
                  </button>
                </div>
                <label class="duration-custom-field"
                  >Segundos
                  <input
                    type="number"
                    min="0"
                    v-model="customDurationValue"
                    placeholder="ex: 90"
                  />
                </label>
                <div v-if="customDurationError" class="duration-custom-error">
                  {{ customDurationError }}
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="applyCustomDuration"
                >
                  OK
                </button>
              </div>
            </b-dropdown-form>
          </b-dropdown>

          <!-- [TEL-1945] filtros aplicam na hora (sem botões Aplicar/Limpar); só o indicador de carregamento -->
          <div class="report-filter-actions d-flex align-items-center mb-2">
            <img
              v-show="loading"
              class="mr-2"
              src="/static/images/loading.gif"
              width="28"
              height="28"
              alt="Carregando dados, aguarde..."
              title="Carregando dados, aguarde..."
            />
          </div>
        </div>
        <!-- chips de filtros ativos (com remoção individual) — agora em Chamadas E Usuários -->
        <div
          v-if="hasVisibleChips"
          class="row mt-3 mb-3"
        >
          <div class="col-12">
            <div class="active-filters-tags">
              <!-- chip do filtro de Período — só quando editado (diferente de Hoje) -->
              <span
                v-if="isDateFiltered"
                class="filter-tag"
              >
                Período: {{ dateFilterLabel }}
                <i
                  class="ik ik-x cursor"
                  @click="removeDateFilter"
                  title="Remover filtro"
                />
              </span>
              <!-- [TEL-1945] chip do filtro de Status (só existe no relatório de Usuários) -->
              <span
                v-if="reportType === 'agents' && usersStatusFilter.length"
                class="filter-tag"
              >
                Status: {{ usersStatusFilter.map(s => statusLabel(s)).join(', ') }}
                <i
                  class="ik ik-x cursor"
                  @click="usersStatusFilter = []"
                  title="Remover filtro"
                />
              </span>
              <span
                v-for="chip in visibleFilterChips"
                :key="chip.key"
                class="filter-tag"
              >
                {{ getFilterLabel(chip.key) }}:
                {{ chip.display }}
                <i
                  class="ik ik-x cursor"
                  @click="removeFilter(chip.key)"
                  title="Remover filtro"
                />
              </span>
            </div>
          </div>
        </div>
        <!-- bloco da tabela de Chamadas (tabela + rodapé) só no tipo Chamadas; wrapper sem d-flex p/ o v-show funcionar -->
        <div v-show="reportType === 'calls'">
          <div class="card-table text-left">
            <!-- removido striped (sem zebra) — visual limpo do design system -->
            <b-table
              class="table"
              ref="cdrTable"
              show-empty
              empty-text="Nenhum registro encontrado"
              empty-filtered-text="Nenhum registro encontrado"
              hover
              small
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :items="items"
              :fields="getFilteredFields()"
              :current-page="currentPage"
              :per-page="perPage"
              :filter="filter"
              :tbody-tr-class="ia4comRowClass"
              @filtered="onFiltered"
            >
              <!-- engrenagem de colunas no header da última coluna; slot HEAD[key] (bootstrap-vue rc.28) -->
              <template slot="HEAD[__cols__]">
                <div class="d-flex justify-content-end">
                  <b-dropdown
                    right
                    no-caret
                    boundary="window"
                    :popper-opts="{ positionFixed: true }"
                    variant="outline-secondary"
                    size="sm"
                    toggle-class="column-gear-btn"
                    menu-class="column-picker-menu"
                    title="Escolher colunas"
                  >
                    <template slot="button-content">
                      <i class="ik ik-settings" />
                    </template>
                    <b-dropdown-form @submit.prevent>
                      <b-form-checkbox-group
                        v-model="visibleColumns"
                        :options="columnOptions"
                        stacked
                      />
                    </b-dropdown-form>
                  </b-dropdown>
                </div>
              </template>
              <!-- [TEL-1945] Gravação: botão (ícone link externo + "Abrir") -->
              <template slot="record_url" slot-scope="row">
                <button
                  v-if="hasRecordUrl(row.item)"
                  type="button"
                  class="report-cell-btn"
                  title="Abrir gravação em nova guia"
                  @click.prevent="download(row.item.record_url)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                    />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  Abrir
                </button>
              </template>
              <!-- [TEL-1945] Metadado: botão (ícone lupa + "Abrir") -->
              <template slot="metadata" slot-scope="row">
                <button
                  v-if="hasMetadata(row.item)"
                  type="button"
                  class="report-cell-btn"
                  title="Abrir metadados"
                  @click="openShowMetadata(row.item)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  Abrir
                </button>
              </template>
              <!-- [TEL-1945/2146] coluna Resumo gerencial — o texto vem de GET /calls/:id/ai, buscado
                   ao clicar. Sem lote no backend, saber de antemão quais linhas têm IA custaria uma
                   request por linha da página, então o botão é sempre oferecido. -->
              <template slot="summary" slot-scope="row">
                <button
                  type="button"
                  class="report-cell-btn report-cell-btn--ai"
                  title="Ver resumo gerencial (IA)"
                  @click="toggleDetail(row.item, 'summary')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10 1.5l1.7 4.8 4.8 1.7-4.8 1.7L10 14.5 8.3 9.7 3.5 8l4.8-1.7L10 1.5z"
                    />
                    <path
                      d="M16 11.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z"
                    />
                  </svg>
                  Ver
                </button>
              </template>
              <!-- [TEL-1945/2146] coluna Análise qualitativa — mesma origem do resumo: uma única
                   GET /calls/:id/ai traz os dois campos, então abrir um já deixa o outro em cache -->
              <template slot="evaluation" slot-scope="row">
                <button
                  type="button"
                  class="report-cell-btn report-cell-btn--ai"
                  title="Ver análise qualitativa"
                  @click="toggleDetail(row.item, 'evaluation')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10 1.5l1.7 4.8 4.8 1.7-4.8 1.7L10 14.5 8.3 9.7 3.5 8l4.8-1.7L10 1.5z"
                    />
                    <path
                      d="M16 11.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z"
                    />
                  </svg>
                  Ver
                </button>
              </template>
              <!-- coluna de ações (fim da linha): botão Copiar — copia id, domain, started_at, to e from
                   da chamada como texto "chave: valor" (valores crus da API, p/ colar em ticket/suporte) -->
              <template slot="__cols__" slot-scope="row">
                <!-- [TEL-2327] abre a ligação no IA4COM, no painel ao lado da lista -->
                <button
                  v-if="canOpenInIa4com(row.item)"
                  type="button"
                  class="report-cell-btn report-cell-btn--icon report-cell-btn--ia4com"
                  :class="{ 'report-cell-btn--ia4com-open': isOpenInIa4comPanel(row.item) }"
                  title="Conversar sobre esta ligação no IA4COM"
                  aria-label="Conversar sobre esta ligação no IA4COM"
                  @click="openInIa4com(row.item)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10 1.5l1.7 4.8 4.8 1.7-4.8 1.7L10 14.5 8.3 9.7 3.5 8l4.8-1.7L10 1.5z"
                    />
                    <path
                      d="M16 11.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="report-cell-btn report-cell-btn--icon report-cell-btn--copy"
                  :class="{ 'report-cell-btn--copied': copiedCallId === row.item.id }"
                  :title="copiedCallId === row.item.id ? 'Dados copiados' : 'Copiar dados da chamada (id, domínio, início, destino e ramal)'"
                  :aria-label="copiedCallId === row.item.id ? 'Dados copiados' : 'Copiar dados da chamada (id, domínio, início, destino e ramal)'"
                  @click="copyCallData(row.item)"
                >
                  <svg
                    v-if="copiedCallId === row.item.id"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </template>
              <!-- painel expandido: mostra Resumo gerencial OU Análise qualitativa conforme o botão clicado (b-table tem só 1 row-details) -->
              <template slot="row-details" slot-scope="row">
                <div
                  v-if="row.item._detailType === 'evaluation'"
                  class="report-summary-details report-eval-details"
                >
                  <div class="report-summary-details__header">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      width="16"
                      height="16"
                      class="mr-2"
                    >
                      <path
                        d="M10 1.5l1.7 4.8 4.8 1.7-4.8 1.7L10 14.5 8.3 9.7 3.5 8l4.8-1.7L10 1.5z"
                      />
                      <path
                        d="M16 11.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z"
                      />
                    </svg>
                    <strong>Análise qualitativa da chamada</strong>
                    <button
                      type="button"
                      class="close ml-auto"
                      aria-label="Fechar"
                      @click="toggleDetail(row.item, 'evaluation')"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <p
                    v-if="row.item._aiLoading"
                    class="report-summary-details__text report-summary-details__text--muted mb-0"
                  >
                    Carregando análise…
                  </p>
                  <p
                    v-else-if="row.item._aiError"
                    class="report-summary-details__text report-summary-details__text--error mb-0"
                  >
                    {{ row.item._aiError }}
                  </p>
                  <p
                    v-else-if="!hasEvaluation(row.item)"
                    class="report-summary-details__text report-summary-details__text--muted mb-0"
                  >
                    Esta chamada não tem análise qualitativa gerada pela IA.
                  </p>
                  <!-- [TEL-2256] o texto da analise qualitativa chega em Markdown; markdownToHtml()
                       escapa o conteudo antes de emitir qualquer tag, entao o v-html so
                       recebe a marcacao gerada aqui -->
                  <div
                    v-else
                    class="report-summary-details__text report-summary-details__text--md mb-0"
                    v-html="renderAiText(row.item.evaluation)"
                  ></div>
                </div>
                <div v-else class="report-summary-details">
                  <div class="report-summary-details__header">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      width="16"
                      height="16"
                      class="mr-2"
                    >
                      <path
                        d="M10 1.5l1.7 4.8 4.8 1.7-4.8 1.7L10 14.5 8.3 9.7 3.5 8l4.8-1.7L10 1.5z"
                      />
                    </svg>
                    <strong>Resumo gerencial da ligação</strong>
                    <button
                      type="button"
                      class="close ml-auto"
                      aria-label="Fechar"
                      @click="toggleDetail(row.item, 'summary')"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <p
                    v-if="row.item._aiLoading"
                    class="report-summary-details__text report-summary-details__text--muted mb-0"
                  >
                    Carregando resumo…
                  </p>
                  <p
                    v-else-if="row.item._aiError"
                    class="report-summary-details__text report-summary-details__text--error mb-0"
                  >
                    {{ row.item._aiError }}
                  </p>
                  <p
                    v-else-if="!hasSummary(row.item)"
                    class="report-summary-details__text report-summary-details__text--muted mb-0"
                  >
                    Esta chamada não tem resumo gerado pela IA.
                  </p>
                  <!-- [TEL-2256] o texto do resumo gerencial chega em Markdown; markdownToHtml()
                       escapa o conteudo antes de emitir qualquer tag, entao o v-html so
                       recebe a marcacao gerada aqui -->
                  <div
                    v-else
                    class="report-summary-details__text report-summary-details__text--md mb-0"
                    v-html="renderAiText(row.item.summary)"
                  ></div>
                </div>
              </template>
            </b-table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <div class="d-flex align-items-center">
              <label class="mb-0 mr-2 text-muted">Itens por página:</label>
              <select
                class="form-control form-control-sm per-page-selector"
                v-model="perPage"
              >
                <option
                  v-for="option in pageOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </div>
            <b-pagination
              align="center"
              class="pagination mb-0"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              :limit="10"
            />
          </div>
        </div>
        <!-- Relatório de Usuários: tabela + engrenagem + itens/página + paginação (mesmas ferramentas do Chamadas) -->
        <div v-show="reportType === 'agents'">
          <!-- [TEL-1945] erro do relatório de Usuários: sem isto uma falha de rede era
               indistinguível de "esta conta não tem usuários" -->
          <div
            v-if="usersError"
            class="alert alert-danger"
            role="alert"
          >
            {{ usersError }}
          </div>
          <div class="card-table text-left">
            <b-table
              class="table"
              show-empty
              hover
              small
              :sort-by.sync="usersSortBy"
              :sort-desc.sync="usersSortDesc"
              :items="userReportRows"
              :fields="getUserFilteredFields()"
              :current-page="usersCurrentPage"
              :per-page="usersPerPage"
              empty-text="Nenhum registro encontrado"
              empty-filtered-text="Nenhum registro encontrado"
            >
              <!-- [TEL-1945] coluna Status: badge colorido (Ativo/Pendente/Inativo) -->
              <template slot="status" slot-scope="row">
                <span
                  class="report-status-badge"
                  :class="'report-status-badge--' + row.item.status"
                >
                  {{ statusLabel(row.item.status) }}
                </span>
              </template>
              <!-- [TEL-1945] balão informativo (i) — Contatos únicos -->
              <template slot="HEAD[uniqueContacts]">
                Contatos únicos
                <svg
                  id="tip-unique-contacts"
                  class="report-info-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  tabindex="0"
                  @click.stop
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <b-tooltip
                  target="tip-unique-contacts"
                  triggers="hover focus"
                  placement="top"
                >
                  Mostra para quantos números diferentes o usuário ligou. Ligar
                  várias vezes para o mesmo número não aumenta esse total.
                </b-tooltip>
              </template>
              <!-- [TEL-1945] balão informativo (i) — Rediscagem -->
              <template slot="HEAD[redials]">
                Rediscagem
                <svg
                  id="tip-redials"
                  class="report-info-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  tabindex="0"
                  @click.stop
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <b-tooltip
                  target="tip-redials"
                  triggers="hover focus"
                  placement="top"
                >
                  Quantidade de rediscagens para um mesmo número de destino no
                  período selecionado.
                </b-tooltip>
              </template>
              <!-- engrenagem de colunas do Usuários (mesmo padrão do Chamadas) -->
              <template slot="HEAD[__cols__]">
                <div class="d-flex justify-content-end">
                  <b-dropdown
                    right
                    no-caret
                    boundary="window"
                    :popper-opts="{ positionFixed: true }"
                    variant="outline-secondary"
                    size="sm"
                    toggle-class="column-gear-btn"
                    menu-class="column-picker-menu"
                    title="Escolher colunas"
                  >
                    <template slot="button-content">
                      <i class="ik ik-settings" />
                    </template>
                    <b-dropdown-form @submit.prevent>
                      <b-form-checkbox-group
                        v-model="usersVisibleColumns"
                        :options="usersColumnOptions"
                        stacked
                      />
                    </b-dropdown-form>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <div class="d-flex align-items-center">
              <label class="mb-0 mr-2 text-muted">Itens por página:</label>
              <select
                class="form-control form-control-sm per-page-selector"
                v-model="usersPerPage"
              >
                <option
                  v-for="option in pageOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </div>
            <b-pagination
              align="center"
              class="pagination mb-0"
              v-model="usersCurrentPage"
              :total-rows="userReportRows.length"
              :per-page="usersPerPage"
              :limit="10"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal fade"
      tabindex="-1"
      role="dialog"
      id="showMetadata"
      ref="showMetadata"
      data-keyboard="false"
      data-backdrop="static"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <h1 class="text-center">Metadados</h1>
            <div class="text-center" v-html="showMetadataText" />
          </div>
          <div class="modal-footer">
            <div class="buttons">
              <button
                type="button"
                class="btn btn-primary"
                data-dismiss="modal"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- [TEL-2327] o IA4COM ao lado da lista; so nasce para quem tem o rollout -->
    <PainelLigacao
      v-if="isIa4comAllowed"
      :aberto="ia4comPanelOpen"
      :ligacao="ia4comPanelCall"
      :pedido-em="ia4comRequestedAt"
      @fechar="ia4comPanelOpen = false"
    />
  </div>
</template>

<script>
import loopback from '@/services/loopback'
import { validatePhone } from '@/utils/phone'
// [TEL-2256] o texto de IA vem em Markdown; converter na exibicao (mesma lib do pbxapi)
import { convertMarkdownToHTML } from '@/utils/markdown'
import { escapeHtml } from '@/utils/html'
import { HANGUPCAUSES_MAPPER } from '../../utils/phone'
// dropdown/checkbox do BootstrapVue não são registrados globalmente (só b-table/b-pagination em main.js); importa local como no Dashboard
import {
  BDropdown,
  BDropdownForm,
  BDropdownItem,
  BFormCheckboxGroup
} from 'bootstrap-vue/dist/bootstrap-vue.esm.min'
import { isFeatureAllowed, IA4COM_FEATURE_FLAG } from '@/services/rollout'
import PainelLigacao from '@/components/IA4COM/PainelLigacao.vue'

// Tamanho de página da busca de chamadas. Tem que caber no teto do guard de paginação
// do /calls (API4COM_MAX_PAGINATION_LIMIT, default 500): acima dele a API devolve 400 e
// a tela não lista nada. 500 é o piso garantido, independente do valor da env no ambiente.
//
// O preço é o outro lado: como o front pagina até o fim seguindo meta.nextPage, e o /calls
// limita a 15 requisições por minuto por usuário, o período que cabe numa busca cai de
// ~15.000 para ~7.500 chamadas. Passando disso, a busca é cortada no meio pelo limiter.
const CDR_PAGE_SIZE = 500

// [TEL-1945] colunas de valor: ocultas para organizações de plano ilimitado, onde
// tarifa e custo por ligação não se aplicam
const PRICE_COLUMNS = ['minute_price', 'call_price']

// [TEL-1945] colunas de IA: só existem para contas com IA ativa. Sem o recurso o backend devolve
// {} e a tela diria "esta chamada não tem resumo", sugerindo que a chamada não foi processada
// quando na verdade a conta inteira não contratou IA.
const AI_COLUMNS = ['summary', 'evaluation']

// Limite máximo do filtro de período (mesma regra do Dashboard).
const MAX_FILTER_RANGE_DAYS = 30
const MAX_FILTER_RANGE_MESSAGE = `Intervalo máximo entre as datas é ${MAX_FILTER_RANGE_DAYS} dias`

// [TEL-2327] janela em que um segundo clique na mesma ligação é duplo clique, não "tente de novo"
const IA4COM_REPEAT_CLICK_MS = 2000

// Rótulo que o backend devolve no lugar do número de contato quando o usuário (ou a
// organização) está marcado para não ver o telefone. A linha mascarada vem com
// phoneHidden: true; o rótulo não pode passar pelo parser de telefone.
const HIDDEN_PHONE_LABEL = 'Número oculto'

export default {
  name: 'ReportsCdrs',
  components: {
    // MultiSelect removido; filtros agora usam b-dropdown/checkbox
    BDropdown,
    BDropdownForm,
    BDropdownItem,
    BFormCheckboxGroup,
    PainelLigacao
  },
  data () {
    return {
      // Um indicador por relatorio: sao buscas independentes, e a de Usuarios
      // terminava desligando o "carregando" da paginacao de Chamadas, que segue
      // enchendo a tabela sem nada na tela.
      loadingCalls: false,
      loadingUsers: false,
      perPage: 10,
      pageOptions: [10, 25, 50],
      currentPage: 1,
      totalRows: 1,
      filter: null,
      lastFilter: null,
      startedAt: new Date(new Date().setHours(0, 0, 0, 0)),
      startedAtNew: new Date(new Date().setHours(0, 0, 0, 0)),
      endedAt: new Date(new Date().setHours(0, 0, 0, 0)),
      endedAtNew: new Date(new Date().setHours(0, 0, 0, 0)),
      error: null,
      // Busca paginada interrompida no meio: o que esta na tabela e so parte do periodo.
      cdrsPartial: false,
      fields: {
        started_at: {
          label: 'Data',
          sortable: true,
          // Ordena pelo valor cru (ISO 8601), que já ordena corretamente como string. Com o
          // texto formatado, a b-table compararia "31/07" com "01/08" e concluiria que 31 vem
          // depois — embaralhando qualquer período que cruze a virada do mês.
          sortByFormatted: false,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return this.parseDate(value)
          }
        },
        first_name: {
          label: 'Usuário',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            let completeName = value || ''
            if (item.last_name) completeName += ` ${item.last_name}`
            return completeName
          }
        },
        to: {
          label: 'Número discado',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            if (this.isPhoneHidden(item)) return HIDDEN_PHONE_LABEL
            return this.parsePhoneNumberAsFormatNational(String(value))
          }
        },
        duration: {
          label: 'Duração',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return this.secondsToTimeParser(value)
          }
        },
        hangup_cause: {
          label: 'Motivo do desligamento',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return this.parseHangupCause(value)
          }
        },
        call_type: {
          label: 'Tipo',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return value === 'outbound' ? 'Sainte' : 'Entrante'
          }
        },
        from: {
          label: 'Ramal',
          sortable: true
        },
        ended_at: {
          label: 'Finalizou às',
          sortable: true,
          // Mesmo motivo de started_at: ordenar pelo ISO, não pelo dd/mm/yyyy exibido.
          sortByFormatted: false,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return this.parseDate(value)
          }
        },
        minute_price: {
          label: 'Tarifa',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return value ? `R$ ${value}` : ''
          }
        },
        call_price: {
          label: 'Custo da Ligação',
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          formatter: (value, key, item) => {
            return value ? `R$ ${value}` : ''
          }
        },
        metadata: {
          label: 'Metadado',
          sortable: false,
          sortByFormatted: false,
          filterByFormatted: false
        },
        record_url: {
          label: 'Gravação',
          sortable: false
        },
        // [TEL-1945] preenchida sob demanda por loadAIInsights() a partir de GET /calls/:id/ai
        summary: {
          label: 'Resumo gerencial',
          sortable: false,
          sortByFormatted: false,
          filterByFormatted: false
        },
        // [TEL-1945] idem: vem de GET /calls/:id/ai, no mesmo request do resumo
        evaluation: {
          label: 'Análise qualitativa',
          sortable: false,
          sortByFormatted: false,
          filterByFormatted: false
        },
        // coluna de ações no fim: header carrega a engrenagem de colunas; células do corpo vazias
        __cols__: {
          label: '',
          sortable: false,
          thClass: 'cols-gear-th',
          tdClass: 'cols-gear-td'
        }
      },
      items: [],
      extensions: [],
      showMetadataText: '',
      // id da chamada cujo botão Copiar acabou de ser clicado (feedback "Copiado" por ~1,5s)
      copiedCallId: null,
      copiedCallTimer: null,
      // colunas visíveis na tabela (chaves de fields); default = todas
      visibleColumns: [],
      // tipo de relatório selecionado ('calls' | 'agents'); default 'calls'
      reportType: 'calls',
      // estado do filtro de período (Data)
      dateFilterLabel: 'Hoje',
      datePresets: [
        { text: 'Hoje', name: 'today' },
        { text: 'Ontem', name: 'yesterday' },
        { text: 'Últimos 7 dias', name: 'last7' },
        { text: 'Últimos 30 dias', name: 'last30' },
        { text: 'Esta semana', name: 'thisWeek' },
        { text: 'Este mês', name: 'thisMonth' },
        { text: 'Mês passado', name: 'lastMonth' }
      ],
      customPeriodOpen: false,
      customFrom: '',
      customTo: '',
      customPeriodError: '',
      // ordenação da b-table (client-side); default started_at desc
      sortBy: 'started_at',
      sortDesc: true,
      advancedFilters: {
        from: [],
        to: '',
        callType: '',
        hangupCause: [],
        durationRange: ''
      },
      showAllHangupCauses: false,
      // [TEL-1945] termo de busca do dropdown de Usuários (ex-Ramais)
      extensionSearch: '',
      // [TEL-1945] Duração personalizada: operador (lt/gt), valor em segundos e erro
      customDurationOpen: false,
      customDurationOp: 'lt',
      customDurationValue: '',
      customDurationError: '',
      // [TEL-1945] número digitado, ainda não confirmado — mesmo padrão de customFrom/customTo.
      // Sem ele, o valor meio digitado entrava na próxima busca disparada por outro filtro.
      pendingNumber: '',
      // ordenação da tabela de Usuários (client-side); default Total Chamadas desc
      usersSortBy: 'totalCalls',
      usersSortDesc: true,
      // engrenagem + paginação do Usuários (mesmo padrão do Chamadas)
      usersVisibleColumns: [],
      usersCurrentPage: 1,
      usersPerPage: 10,
      // [TEL-1945] filtro de Status do relatório de Usuários (múltipla escolha)
      usersStatusFilter: [],
      usersStatusOptions: [
        { value: 'ativo', text: 'Ativo' },
        { value: 'pendente', text: 'Pendente' },
        { value: 'inativo', text: 'Inativo' }
      ],
      // colunas da tabela de Usuários (mesmo padrão de fields da tabela de Chamadas)
      userReportFields: {
        name: { label: 'Usuário', sortable: true },
        // O backend devolve `extensions` como array: um usuário pode ter mais de um ramal.
        extensions: {
          label: 'Ramal',
          sortable: true,
          sortByFormatted: true,
          formatter: value => (Array.isArray(value) ? value.join(', ') : value || '')
        },
        status: {
          label: 'Status',
          sortable: true,
          sortByFormatted: true,
          formatter: value => this.statusLabel(value)
        },
        totalCalls: { label: 'Total Chamadas', sortable: true },
        answeredCalls: { label: 'Chamadas Atendidas', sortable: true },
        uniqueContacts: { label: 'Contatos únicos', sortable: true },
        redials: { label: 'Rediscagem', sortable: true },
        hitRate: {
          label: 'Taxa de atendimento',
          sortable: true,
          formatter: value => `${value}%`
        },
        talkTimeSeconds: {
          label: 'Tempo Total em Atendimento',
          sortable: true,
          formatter: value => this.secondsToTimeParser(value)
        },
        averageTalkTimeSeconds: {
          label: 'Tempo médio de atendimento',
          sortable: true,
          formatter: value => this.secondsToTimeParser(value)
        },
        // coluna de ações (engrenagem de colunas do Usuários)
        __cols__: {
          label: '',
          sortable: false,
          thClass: 'cols-gear-th',
          tdClass: 'cols-gear-td'
        }
      },
      // [TEL-1945] relatório de Usuários vem do backend via GET /reports/agents (agregação por atendente é do backend, não do front)
      userReportItems: [],
      // [TEL-1945] os dois relatórios compartilham a barra de filtros, mas cada um carrega
      // sozinho. Estas flags marcam qual deles ficou para trás quando o filtro mudou com a
      // outra aba aberta — sem elas, trocar de aba mostra o recorte anterior com o chip novo.
      usersReportLoaded: false,
      callsReportStale: false,
      // erro do relatório de Usuários (a barra de erro do topo é do relatório de Chamadas)
      usersError: null,
      usersExporting: false,
      // [TEL-2327] o botão do IA4COM por ligação. Começa negado: erro de rede no rollout
      // não pode mostrar o botão (services/rollout.js responde negado em qualquer falha).
      isIa4comAllowed: false,
      ia4comPanelOpen: false,
      // { callId, inicio, vendedor } da ligação aberta no painel (o formato que PainelLigacao lê)
      ia4comPanelCall: null,
      // muda a cada pedido de ligação — ver `pedidoEm` em IA4COM/QuadroIA4COM.vue
      ia4comRequestedAt: 0
    }
  },
  created () {
    isFeatureAllowed(IA4COM_FEATURE_FLAG).then(allowed => {
      this.isIa4comAllowed = allowed
    })
  },
  mounted () {
    this.$nextTick(function () {
      // inicializa colunas visíveis com todas as disponíveis
      this.visibleColumns = this.availableColumnKeys
      // colunas visíveis iniciais do Usuários
      this.usersVisibleColumns = this.usersAvailableColumnKeys
      this.getCdrs()
      this.loadExtensions()
    })
  },
  beforeDestroy () {
    // [TEL-1945] o debounce dos filtros nao pode disparar depois que a tela saiu
    clearTimeout(this._refreshTimer)
    // idem p/ o feedback "Copiado" do botão de copiar dados da chamada
    clearTimeout(this.copiedCallTimer)
  },
  methods: {
    // [TEL-1945] guard central de intervalo — presets nunca excedem 30 dias; protege custom/edge cases (adaptado do antigo limite de 30 dias)
    exceedsMaxRange (startDate, endDate) {
      if (!startDate || !endDate) return false
      const diffInDays = Math.floor((endDate - startDate) / 86400000)
      return diffInDays > MAX_FILTER_RANGE_DAYS
    },
    getCdrs () {
      this.loadingCalls = true
      this.items = []
      this.cdrsPartial = false
      this.currentPage = 1
      // [TEL-1945] cada busca ganha um número; respostas de buscas anteriores são descartadas.
      // Como os filtros aplicam ao clique, trocar de filtro no meio do carregamento paginado
      // deixava duas cadeias vivas concatenando no mesmo array — a tabela (e o CSV) acabavam
      // com linhas de dois recortes diferentes, sem nenhum aviso.
      const requestId = (this._cdrRequestId || 0) + 1
      this._cdrRequestId = requestId
      this.callsReportStale = false
      const startDate = this.startedAtNew || this.startedAt
      const endDate = this.endedAtNew || this.endedAt

      // [TEL-1945] guard de segurança de intervalo (presets/custom)
      if (this.exceedsMaxRange(startDate, endDate)) {
        this.loadingCalls = false
        this.error = { message: MAX_FILTER_RANGE_MESSAGE }
        return
      }

      const start = `${startDate.toJSON().slice(0, 11)}00:00:00Z`
      const end = `${endDate.toJSON().slice(0, 11)}23:59:59Z`

      this.getCdrsFromApi(start, end, 1, requestId)
    },
    getCdrsFromApi (start, end, page = 1, requestId = this._cdrRequestId) {
      this.error = null
      const whereClause = {
        and: [{ started_at: { gt: start } }, { ended_at: { lt: end } }]
      }

      // Com o número oculto o filtro some da tela e não vai para a API (o backend também o
      // ignora): filtrar por um número que a pessoa não pode ver revelaria o número.
      if (this.advancedFilters.to && !this.isPhoneNumberHidden) {
        const normalizedForApi = this.parsePhoneNumberAsNationalNumber(
          this.advancedFilters.to
        )
        whereClause.and.push({ to: { like: `%${normalizedForApi}%` } })
      }

      if (this.advancedFilters.callType) {
        whereClause.and.push({ call_type: this.advancedFilters.callType })
      }

      if (this.advancedFilters.durationRange) {
        const [
          minDuration,
          maxDuration
        ] = this.advancedFilters.durationRange.split('-').map(Number)
        if (minDuration !== undefined && !isNaN(minDuration)) {
          whereClause.and.push({ duration: { gte: minDuration } })
        }
        if (
          maxDuration !== undefined &&
          !isNaN(maxDuration) &&
          maxDuration < 999999
        ) {
          whereClause.and.push({ duration: { lt: maxDuration } })
        }
      }

      if (this.advancedFilters.from && this.advancedFilters.from.length > 0) {
        if (this.advancedFilters.from.length === 1) {
          whereClause.and.push({ from: this.advancedFilters.from[0] })
        } else {
          const fromConditions = this.advancedFilters.from.map(extension => ({
            from: extension
          }))
          whereClause.and.push({ or: fromConditions })
        }
      }

      if (
        this.advancedFilters.hangupCause &&
        this.advancedFilters.hangupCause.length > 0
      ) {
        const hangupConditions = []
        this.advancedFilters.hangupCause.forEach(selectedCause => {
          const selectedText = HANGUPCAUSES_MAPPER[selectedCause]
          const allCausesWithSameText = Object.entries(HANGUPCAUSES_MAPPER)
            .filter(([cause, text]) => text === selectedText)
            .map(([cause]) => cause)

          allCausesWithSameText.forEach(cause => {
            hangupConditions.push({ hangup_cause: cause })
          })
        })

        if (hangupConditions.length > 0) {
          whereClause.and.push({ or: hangupConditions })
        }
      }

      const completeFilter = {
        where: whereClause,
        limit: CDR_PAGE_SIZE,
        order: 'started_at desc'
      }

      const filterParam = encodeURIComponent(JSON.stringify(completeFilter))
      const finalUrl = `/calls?page=${page}&filter=${filterParam}`

      return loopback
        .get(finalUrl)
        .then(cdrs => {
          // Busca superada por outra mais recente: não escreve nada, nem apaga o spinner dela.
          if (requestId !== this._cdrRequestId) return
          if (cdrs) {
            if (cdrs.data) {
              this.items = this.items.concat(Array.from(cdrs.data))
              this.totalRows = this.items.length
            }
            if (cdrs.meta && cdrs.meta.nextPage) { return this.getCdrsFromApi(start, end, cdrs.meta.nextPage, requestId) }
            this.loadingCalls = false
          }
        })
        .catch(err => {
          if (requestId !== this._cdrRequestId) return
          this.loadingCalls = false
          this.error = err
          // A busca é paginada: um erro no meio deixa as páginas anteriores na tabela.
          // Sem marcar, o recorte parcial passa por completo — e o CSV sai truncado sem
          // nada indicando isso. Fica mais provável desde que a página caiu para 500,
          // porque o limiter de 15/min do /calls passou a ser atingido antes.
          this.cdrsPartial = this.items.length > 0
        })
    },
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      if (this.filter !== this.lastFilter) {
        this.currentPage = 1
        this.lastFilter = this.filter
      }
    },
    hasRecordUrl (record) {
      if (
        record &&
        record.hangup_cause === 'NORMAL_CLEARING' &&
        record.record_url &&
        record.record_url.length > 0
      ) { return true }
      return false
    },
    /**
     * [TEL-2327] Só ligação concluída com gravação, e só a própria — salvo ADMIN.
     *
     * Diverge DE PROPÓSITO do escopo do relatório: o perfil USER no plano Gerencial vê
     * as ligações do time aqui, mas o pbxapi (POST /calls/:id/context) responde 404 nas
     * alheias. O botão some nessas linhas para não oferecer o que vai ser recusado. Quem
     * autoriza continua sendo o pbxapi; isto só evita o clique sem saída.
     */
    canOpenInIa4com (record) {
      if (!this.isIa4comAllowed || !this.hasRecordUrl(record)) return false
      if (this.isAdmin) return true
      const user = this.$store.state.auth && this.$store.state.auth.user
      const userEmail = String((user && user.email) || '').trim().toLowerCase()
      const callEmail = String(record.email || '').trim().toLowerCase()
      return !!userEmail && userEmail === callEmail
    },
    openInIa4com (record) {
      const sameCall = !!(this.ia4comPanelCall && this.ia4comPanelCall.callId === record.id)
      // A MESMA ligação com o painel FECHADO só reabre: cada pedido vira conversa nova no
      // IA4COM (app.vue, abrirLigacao), e fechar e reabrir precisa manter a conversa.
      if (sameCall && !this.ia4comPanelOpen) {
        this.ia4comPanelOpen = true
        return
      }
      // Com o painel ABERTO nela, o clique é o "tente de novo" que o IA4COM pede depois de uma
      // falha. Mas um segundo clique logo em seguida é duplo clique, não "tente de novo": cada
      // pedido abre conversa nova no IA4COM e gasta cota. Um "tente de novo" de verdade vem
      // segundos depois de a mensagem de erro aparecer.
      if (sameCall && Date.now() - this.ia4comRequestedAt < IA4COM_REPEAT_CLICK_MS) return
      this.ia4comPanelCall = {
        callId: record.id,
        inicio: this.parseDate(record.started_at),
        vendedor: `${record.first_name || ''} ${record.last_name || ''}`.trim()
      }
      this.ia4comRequestedAt = Date.now()
      this.ia4comPanelOpen = true
    },
    isOpenInIa4comPanel (record) {
      return !!(this.ia4comPanelOpen && record && this.ia4comPanelCall &&
        this.ia4comPanelCall.callId === record.id)
    },
    // a linha da ligação aberta fica marcada enquanto o painel está aberto
    ia4comRowClass (item, type) {
      return type === 'row' && this.isOpenInIa4comPanel(item) ? 'report-row--ia4com' : null
    },
    hasMetadata (record) {
      // linha com número oculto não abre metadado (vale mesmo com o store desatualizado)
      if (this.isPhoneHidden(record)) return false
      if (record && record.metadata && typeof record.metadata === 'object') { return true }
      return false
    },
    // [TEL-1945] só há resumo depois que loadAIInsights() responde; antes disso o painel
    // mostra o estado de carregando
    hasSummary (record) {
      return !!(
        record &&
        typeof record.summary === 'string' &&
        record.summary.trim().length > 0
      )
    },
    // [TEL-2256] converte o Markdown do texto de IA em HTML para exibicao no painel.
    // O markdown-it esta configurado com `html: false` em @/utils/markdown, entao o resultado e
    // seguro para v-html mesmo vindo de um LLM.
    renderAiText (text) {
      return convertMarkdownToHTML(text)
    },
    // [TEL-1945] idem para a análise qualitativa
    hasEvaluation (record) {
      return !!(
        record &&
        typeof record.evaluation === 'string' &&
        record.evaluation.trim().length > 0
      )
    },
    // abre/fecha o painel (Resumo ou Avaliação) abaixo da linha via row-details da b-table.
    // b-table só tem 1 row-details, então _detailType define qual conteúdo mostrar.
    toggleDetail (record, type) {
      if (record._showDetails && record._detailType === type) {
        this.$set(record, '_showDetails', false)
      } else {
        this.$set(record, '_detailType', type)
        this.$set(record, '_showDetails', true)
        this.loadAIInsights(record)
      }
    },
    // [TEL-2146] resumo e avaliação de IA da chamada, sob demanda. Uma request traz os dois
    // campos, então o cache por linha (_aiLoaded) evita repetir ao alternar entre os painéis.
    async loadAIInsights (record) {
      if (record._aiLoaded || record._aiLoading) return
      this.$set(record, '_aiLoading', true)
      this.$set(record, '_aiError', null)
      try {
        const response = await loopback.get(`/calls/${record.id}/ai`)
        // O backend responde em português (resumo/avaliacao); a tabela usa summary/evaluation.
        this.$set(record, 'summary', (response && response.resumo) || '')
        this.$set(record, 'evaluation', (response && response.avaliacao) || '')
        this.$set(record, '_aiLoaded', true)
      } catch (e) {
        // 503 do pbxapi quando o connectors-service não responde: distinguir de "sem IA" é o
        // ponto do contrato — mostrar "sem resumo" para dado que existe seria pior que o erro.
        // O `publicMessage` do backend existe e é whitelistado, mas vem em inglês; o texto
        // exibido fica aqui para não misturar idioma na tela.
        this.$set(
          record,
          '_aiError',
          'Não foi possível carregar os dados de IA desta chamada. Tente novamente.'
        )
      } finally {
        this.$set(record, '_aiLoading', false)
      }
    },
    openShowMetadata (record) {
      if (this.isPhoneHidden(record)) return
      if (record && record.metadata && typeof record.metadata === 'object') {
        let metadataAsString = ''
        const keys = Object.keys(record.metadata)
        if (keys && keys.length > 0) {
          metadataAsString +=
            '<table class="table table-striped table-hover table-sm"><tr><th>Chave</th><th>Valor</th></tr>'
          // O metadado chega de quem disparou a ligação pela API: escapado, porque vai para
          // `v-html`. Cru, um valor com <img onerror> executava script no portal e lia o
          // token de sessão do localStorage.
          keys.forEach(key => {
            metadataAsString += `<tr><td>${escapeHtml(key)}</td><td>${escapeHtml(record.metadata[key])}</td></tr>`
          })
          metadataAsString += '</table>'
        }
        this.showMetadataText = metadataAsString
      } else {
        this.showMetadataText = ''
      }
      $(this.$refs.showMetadata).modal('show')
    },
    getMetadataToCSV (metadata) {
      if (metadata && typeof metadata === 'object') {
        let metadataAsString = ''
        const keys = Object.keys(metadata)
        if (keys && keys.length > 0) {
          keys.forEach(key => {
            metadataAsString += `,${key}:${metadata[key]}`
          })
          metadataAsString = metadataAsString.slice(1)
        }
        return metadataAsString
      }
      return ''
    },
    parseDate (date) {
      if (date) { return new Date(date).toLocaleString('pt-BR', { timeZone: 'UTC' }) }
      return ''
    },
    secondsToTimeParser (seconds) {
      seconds = parseInt(seconds)
      // Campo ausente ou não numérico vira 00:00:00: sem isto, parseInt(undefined) é NaN e o
      // twoDigitsParser devolve 'aN' (de `0NaN`.substr(-2)), exibindo aN:aN:aN na tabela.
      if (isNaN(seconds) || seconds < 0) return '00:00:00'
      const hours = parseInt(seconds / 3600)
      seconds = seconds % 3600
      const minutes = parseInt(seconds / 60)
      seconds = seconds % 60
      return `${this.twoDigitsParser(hours)}:${this.twoDigitsParser(
        minutes
      )}:${this.twoDigitsParser(seconds)}`
    },
    parseHangupCause (hangupCause) {
      return HANGUPCAUSES_MAPPER[hangupCause] || hangupCause
    },
    parsePhoneNumberAsFormatNational (value) {
      const phoneParsed = validatePhone(String(value))

      if (phoneParsed && phoneParsed.formatted) {
        return phoneParsed.formatted
      }

      return value
    },
    parsePhoneNumberAsNationalNumber (number) {
      const phone = validatePhone(number)
      if (phone && phone.nationalNumber) return phone.nationalNumber
      return number
    },
    // [TEL-1945] valor de cada coluna no CSV. A chave e a mesma de `fields`, para o arquivo
    // seguir a engrenagem sem uma segunda lista para manter em sincronia.
    csvValueFor (key, item) {
      switch (key) {
        case 'started_at': return this.parseDate(item.started_at)
        case 'first_name': return `${item.first_name || ''} ${item.last_name || ''}`.trim()
        case 'to': return this.isPhoneHidden(item)
          ? HIDDEN_PHONE_LABEL
          : this.parsePhoneNumberAsNationalNumber(String(item.to))
        case 'duration': return item.duration
        case 'hangup_cause': return this.parseHangupCause(item.hangup_cause)
        case 'call_type': return item.call_type === 'outbound' ? 'Sainte' : 'Entrante'
        case 'from': return item.from
        case 'ended_at': return this.parseDate(item.ended_at)
        case 'minute_price': return item.minute_price ? item.minute_price : ''
        case 'call_price': return item.call_price ? item.call_price : ''
        case 'record_url': return item.record_url ? item.record_url : ''
        case 'metadata': return item.metadata && !this.isPhoneHidden(item) ? this.getMetadataToCSV(item.metadata) : ''
        default: return ''
      }
    },
    csvExport () {
      // Recorte parcial não vira arquivo: fora da tela, nada distingue um CSV truncado de
      // um completo, e ele acaba virando base de planilha e automação.
      if (this.cdrsPartial) return
      // BOM: sem ele o Excel em pt-BR abre o arquivo como ANSI e os acentos chegam quebrados.
      let csvContent = 'data:text/csv;charset=utf-8,\uFEFF'
      // As colunas do arquivo sao as que estao na tela: getFilteredFields ja aplica a
      // engrenagem, o plano ilimitado e o gate de IA, na ordem original de `fields`.
      //
      // Resumo gerencial e Analise qualitativa ficam de fora mesmo quando visiveis: o texto
      // e buscado por linha, sob demanda, ao expandir o painel — exportar traria a coluna
      // vazia para quase todas as linhas, o que e pior que nao ter a coluna. A exportacao
      // com IA existe pronta no backend (TEL-2165), que busca em lote.
      const columns = Object.keys(this.getFilteredFields()).filter(
        key => !AI_COLUMNS.includes(key) && key !== '__cols__'
      )
      // A coluna ID entra sempre, fora da engrenagem. Sem esta guarda, desmarcar
      // todas baixaria um arquivo so com ela — o mesmo caso de borda ja tratado
      // na exportacao do relatorio de Usuarios.
      if (!columns.length) {
        this.error = { message: 'Selecione ao menos uma coluna na engrenagem para exportar.' }
        return
      }
      // O cabecalho acompanha o da tela, com tres excecoes que preservam o nome historico da
      // coluna no arquivo: clientes importam este CSV em planilha e automacao, e renomear
      // cabecalho quebra isso em silencio. 'Atendente' -> 'Usuario' e a unica renomeacao que
      // entra, porque foi pedida no review (requisito 5 do card).
      const CSV_HEADER_OVERRIDES = {
        started_at: 'Iniciou às',
        hangup_cause: 'Causa do desligamento',
        metadata: 'Metadados'
      }
      const csvHeaderFor = key => CSV_HEADER_OVERRIDES[key] || this.fields[key].label
      const headers = ['ID', ...columns.map(csvHeaderFor)]
      csvContent += [
        headers.join(';'),
        ...this.items.map(item =>
          [item.id, ...columns.map(key => this.csvValueFor(key, item))].join(';')
        )
      ]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '')
      const data = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', data)
      link.setAttribute('download', 'export.csv')
      link.click()
    },
    download (recordUrl) {
      window.open(recordUrl, 'download')
    },
    // texto copiado pelo botão Copiar da linha: uma linha "chave: valor" por campo, valores crus da API
    buildCallClipboardText (call) {
      return ['id', 'domain', 'started_at', 'to', 'from']
        .map(key => {
          // linha mascarada copia o rótulo, nunca o número
          if (key === 'to' && this.isPhoneHidden(call)) return `${key}: ${HIDDEN_PHONE_LABEL}`
          return `${key}: ${call[key] == null ? '' : call[key]}`
        })
        .join('\n')
    },
    // linha com o número de contato mascarado pelo backend; comparação estrita para que só
    // a marca explícita troque o número pelo rótulo
    isPhoneHidden (call) {
      return !!(call && call.phoneHidden === true)
    },
    // navigator.clipboard só existe em contexto seguro (https/localhost); fora dele cai no execCommand
    writeToClipboard (text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text)
      }
      return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        let copied = false
        try {
          copied = document.execCommand('copy')
        } catch (e) {
          copied = false
        }
        document.body.removeChild(textarea)
        copied ? resolve() : reject(new Error('copy command failed'))
      })
    },
    copyCallData (call) {
      this.writeToClipboard(this.buildCallClipboardText(call))
        .then(() => {
          this.copiedCallId = call.id
          clearTimeout(this.copiedCallTimer)
          this.copiedCallTimer = setTimeout(() => {
            this.copiedCallId = null
          }, 1500)
        })
        .catch(err => {
          console.error('copyCallData error:', err)
        })
    },
    twoDigitsParser (value) {
      return `0${value}`.substr(-2)
    },
    // calcula intervalo (UTC midnight) de um preset de data
    computePresetRange (name) {
      const now = new Date()
      const y = now.getFullYear()
      const m = now.getMonth()
      const d = now.getDate()
      const at = (yy, mm, dd) => new Date(Date.UTC(yy, mm, dd, 0, 0, 0))
      const today = at(y, m, d)
      if (name === 'today') return { start: today, end: today }
      if (name === 'yesterday') {
        const t = at(y, m, d)
        t.setUTCDate(t.getUTCDate() - 1)
        return { start: t, end: new Date(t) }
      }
      if (name === 'last7') {
        const s = at(y, m, d)
        s.setUTCDate(s.getUTCDate() - 7)
        return { start: s, end: today }
      }
      if (name === 'last30') {
        const s = at(y, m, d)
        s.setUTCDate(s.getUTCDate() - 30)
        return { start: s, end: today }
      }
      if (name === 'thisWeek') {
        const day = now.getDay()
        const s = at(y, m, d)
        s.setUTCDate(s.getUTCDate() - (day === 0 ? 6 : day - 1))
        return { start: s, end: today }
      }
      if (name === 'thisMonth') return { start: at(y, m, 1), end: today }
      if (name === 'lastMonth') { return { start: at(y, m - 1, 1), end: at(y, m, 0) } }
      return { start: today, end: today }
    },
    // seleciona preset de data (pendente até "Aplicar filtros")
    selectDatePreset (preset) {
      const range = this.computePresetRange(preset.name)
      this.startedAtNew = range.start
      this.endedAtNew = range.end
      this.dateFilterLabel = preset.text
      this.customPeriodOpen = false
      this.customPeriodError = ''
      // [TEL-1945] aplica na hora
      this.scheduleRefresh()
    },
    // aplica período personalizado (valida + regra de 30 dias)
    applyCustomPeriod () {
      if (!this.customFrom || !this.customTo) {
        this.customPeriodError = 'Selecione as duas datas.'
        return
      }
      if (this.customTo < this.customFrom) {
        this.customPeriodError = 'A data fim não pode ser anterior à início.'
        return
      }
      const [sy, sm, sd] = this.customFrom.split('-').map(Number)
      const [ey, em, ed] = this.customTo.split('-').map(Number)
      const start = new Date(Date.UTC(sy, sm - 1, sd, 0, 0, 0))
      const end = new Date(Date.UTC(ey, em - 1, ed, 0, 0, 0))
      const diffInDays = Math.floor((end - start) / 86400000)
      if (diffInDays > MAX_FILTER_RANGE_DAYS) {
        this.customPeriodError = MAX_FILTER_RANGE_MESSAGE
        return
      }
      this.customPeriodError = ''
      this.startedAtNew = start
      this.endedAtNew = end
      this.dateFilterLabel = `${this.customFrom
        .split('-')
        .reverse()
        .join('/')} - ${this.customTo
        .split('-')
        .reverse()
        .join('/')}`
      if (this.$refs.dateDropdown) this.$refs.dateDropdown.hide()
      // [TEL-1945] aplica na hora
      this.scheduleRefresh()
    },
    // [TEL-1945] Número discado aplica ao clicar OK / Enter (não a cada tecla)
    applyNumberFilter () {
      this.advancedFilters.to = this.pendingNumber.trim()
      if (this.$refs.numberDropdown) this.$refs.numberDropdown.hide()
      // scheduleRefresh e nao refreshCurrentReport: confirmar o numero logo depois
      // de mexer em outro filtro dispararia duas buscas completas, e cada uma delas
      // gasta varias das 15 requisicoes/minuto que o /calls permite.
      this.scheduleRefresh()
    },
    // [TEL-1945] aplica duração personalizada (menor/maior que X segundos) via advancedFilters.durationRange
    applyCustomDuration () {
      const secs = parseInt(this.customDurationValue, 10)
      if (this.customDurationValue === '' || isNaN(secs) || secs < 0) {
        this.customDurationError = 'Informe os segundos.'
        return
      }
      this.customDurationError = ''
      // [TEL-1945] "maior que N" precisa ser estrito: a query monta duration >= min, entao
      // sem o +1 o filtro "maior que 50" devolvia as chamadas de exatamente 50s. O +1 fica aqui
      // e nao no gte porque o gte tambem serve aos presets, onde "entre 1 e 5 min" (60-300) deve
      // mesmo incluir as de 60s.
      this.advancedFilters.durationRange =
        this.customDurationOp === 'lt' ? `0-${secs}` : `${secs + 1}-999999`
      this.customDurationOpen = false
      if (this.$refs.durationDropdown) this.$refs.durationDropdown.hide()
    },
    // [TEL-1945] rótulo legível de um durationRange (presets + personalizado menor/maior que)
    formatDurationRange (value) {
      const map = {
        '': 'Duração',
        '0-60': 'Até 1 minuto',
        '60-300': 'Entre 1 e 5 min',
        '300-600': 'Entre 5 e 10 min',
        '600-999999': 'Maior que 10 min'
      }
      if (map[value] !== undefined) return map[value]
      const parts = String(value).split('-').map(Number)
      const min = parts[0]
      const max = parts[1]
      if (min === 0 && !isNaN(max)) return `Menor que ${max}s`
      // O "maior que" e guardado como min+1 para ser estrito (ver applyCustomDuration);
      // o rotulo desfaz isso e mostra o numero que a pessoa digitou.
      if (max === 999999 && !isNaN(min)) return `Maior que ${min - 1}s`
      return value
    },
    getFilteredFields () {
      // monta os campos só com as colunas em visibleColumns, preservando a ordem original de fields
      const filtered = Object.keys(this.fields).reduce((acc, fieldKey) => {
        // __cols__ (ações/engrenagem) é tratado à parte, sempre no fim
        if (fieldKey === '__cols__') return acc
        if (!this.visibleColumns.includes(fieldKey)) return acc
        // [TEL-1945] guarda de renderização: visibleColumns é semeado no mounted, quando a
        // organização pode ainda não ter chegado ao store. Sem isto, uma conta ilimitada que
        // carregasse depois manteria tarifa/custo na tabela.
        if (this.isUnlimited && PRICE_COLUMNS.includes(fieldKey)) return acc
        if (!this.hasAI && AI_COLUMNS.includes(fieldKey)) return acc
        // número oculto: o metadado é texto livre da integração do cliente e pode trazer o
        // telefone do contato — a coluna some da tabela e, por consequência, do CSV
        if (this.isPhoneNumberHidden && fieldKey === 'metadata') return acc
        acc[fieldKey] = this.fields[fieldKey]
        return acc
      }, {})
      // coluna de engrenagem sempre presente, ao final
      filtered.__cols__ = this.fields.__cols__
      return filtered
    },
    // [TEL-1945] mapeia o valor de status do usuário para o rótulo exibido
    statusLabel (value) {
      const map = { ativo: 'Ativo', pendente: 'Pendente', inativo: 'Inativo' }
      return map[value] || value || '—'
    },
    // colunas do Usuários filtradas por usersVisibleColumns; __cols__ sempre ao fim
    getUserFilteredFields () {
      const filtered = Object.keys(this.userReportFields).reduce((acc, key) => {
        if (key === '__cols__') return acc
        if (!this.usersVisibleColumns.includes(key)) return acc
        acc[key] = this.userReportFields[key]
        return acc
      }, {})
      filtered.__cols__ = this.userReportFields.__cols__
      return filtered
    },
    getActiveFilters () {
      const active = {}
      Object.keys(this.advancedFilters).forEach(key => {
        const value = this.advancedFilters[key]
        if (Array.isArray(value) && value.length > 0) {
          active[key] = value
        } else if (value && value !== '') {
          active[key] = value
        }
      })
      return active
    },
    getFilterLabel (key) {
      const labels = {
        from: 'Usuário',
        to: 'Número Discado',
        callType: 'Tipo de Chamada',
        hangupCause: 'Motivo do Desligamento',
        durationRange: 'Duração'
      }
      return labels[key] || key
    },
    getFilterDisplayValue (key, value) {
      switch (key) {
        case 'callType':
          return value === 'outbound'
            ? 'Sainte'
            : value === 'inbound'
              ? 'Entrante'
              : value
        case 'hangupCause':
          if (Array.isArray(value)) {
            return value.length > 0
              ? value
                .map(cause => HANGUPCAUSES_MAPPER[cause] || cause)
                .join(', ')
              : ''
          }
          return HANGUPCAUSES_MAPPER[value] || value
        case 'from':
          if (Array.isArray(value)) {
            if (value.length === 0) return ''
            // O value é o ramal (é o que a query usa); o chip mostra "Nome (ramal)", como o
            // dropdown, senão quem filtra por vários usuários perde a referência de quem é quem.
            return value
              .map(ramal => {
                const opt = this.extensionOptionsArray.find(o => o.value === ramal)
                return opt ? opt.text : ramal
              })
              .join(', ')
          }
          return value
        case 'durationRange':
          return this.formatDurationRange(value)
        default:
          return value
      }
    },
    // remove o filtro de Período: volta ao default (Hoje) e recarrega
    removeDateFilter () {
      const todayRange = this.computePresetRange('today')
      this.startedAtNew = todayRange.start
      this.endedAtNew = todayRange.end
      this.startedAt = todayRange.start
      this.endedAt = todayRange.end
      this.dateFilterLabel = 'Hoje'
      this.scheduleRefresh()
    },
    removeFilter (key) {
      if (Array.isArray(this.advancedFilters[key])) {
        this.advancedFilters[key] = []
      } else {
        this.advancedFilters[key] = ''
      }
      // 'to' tem campo intermediário; limpar o chip precisa limpar o que está digitado também.
      if (key === 'to') this.pendingNumber = ''
      this.currentPage = 1
      // [TEL-1945] from/callType/hangupCause/durationRange refazem via watcher; 'to' precisa de fetch explícito
      if (key === 'to') this.scheduleRefresh()
    },
    // [TEL-1945] filtros do relatório de Usuários, na forma que o backend espera. Fica num
    // método só porque a tela e a exportação PRECISAM aplicar o mesmo recorte: duas montagens
    // separadas divergiriam no primeiro filtro que alguém adicionasse em um dos lados, e o
    // arquivo deixaria de bater com o que está na tela.
    agentsReportParams () {
      const startDate = this.startedAtNew || this.startedAt
      const endDate = this.endedAtNew || this.endedAt
      const params = {
        startedAt: `${startDate.toJSON().slice(0, 11)}00:00:00Z`,
        endedAt: `${endDate.toJSON().slice(0, 11)}23:59:59Z`
      }
      // O backend (TEL-2164) le `users` e `status` como array JSON na query string; o axios
      // cuida do encode. `users` sao ramais — e o valor do dropdown de Usuário.
      if (this.advancedFilters.from.length) {
        params.users = JSON.stringify(this.advancedFilters.from)
      }
      if (this.usersStatusFilter.length) {
        params.status = JSON.stringify(this.usersStatusFilter)
      }
      // Sem estes, o backend cai no default dele (mais chamadas primeiro) e o arquivo
      // exportado sai numa ordem diferente da que está na tela.
      if (this.usersSortBy) {
        params.sortBy = this.usersSortBy
        params.sortDir = this.usersSortDesc ? 'desc' : 'asc'
      }
      return params
    },
    // [TEL-1945] exportação do relatório de Usuários. Diferente da de Chamadas, que monta o CSV
    // aqui, esta pede o arquivo pronto ao backend (TEL-2165): ele já aplica o mesmo recorte,
    // resolve as colunas e devolve o CSV com BOM. Montar de novo no cliente seria uma segunda
    // implementação do mesmo arquivo para manter em sincronia.
    async exportUsersReport () {
      if (this.usersExporting) return
      this.usersExporting = true
      this.usersError = null
      try {
        const params = this.agentsReportParams()
        // As colunas do arquivo são as que estão na tela. As chaves do relatório de Usuários
        // são as mesmas do catálogo do backend (AGENTS_COLUMNS), então não há tradução.
        const columns = Object.keys(this.getUserFilteredFields()).filter(key => key !== '__cols__')
        // Lista vazia, para o backend, significa "todas" (resolveColumns devolve o catálogo
        // inteiro) — o arquivo sairia completo, com E-mail inclusive, que a tela não mostra.
        // Como não há como pedir "nenhuma", exportar aqui não faz sentido.
        if (!columns.length) {
          this.usersError = 'Selecione ao menos uma coluna na engrenagem para exportar.'
          return
        }
        params.columns = JSON.stringify(columns)
        const blob = await loopback.get('/reports/agents/export', {
          params,
          responseType: 'blob'
        })
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }))
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', this.usersExportFilename(params))
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        // Sem o revoke o blob fica retido na aba até o reload.
        window.URL.revokeObjectURL(url)
      } catch (e) {
        // O corpo do erro vem como blob (responseType), então não dá para ler a mensagem do
        // backend sem convertê-lo — e as duas causas possíveis (período/coluna inválidos ou
        // upstream fora) não mudam o que o usuário pode fazer.
        this.usersError =
          'Não foi possível exportar o relatório de usuários. Tente novamente.'
      } finally {
        this.usersExporting = false
      }
    },
    // O nome do arquivo é montado aqui porque o interceptor do loopback devolve só o corpo da
    // resposta, sem os headers — então o Content-Disposition do backend não chega. Usa as datas
    // como o usuário as escolheu; o backend nomeia pelo fuso do relatório e pode diferir em um
    // dia, o que não muda o conteúdo.
    usersExportFilename (params) {
      return `relatorio-usuarios_${params.startedAt.slice(0, 10)}_${params.endedAt.slice(0, 10)}.csv`
    },
    // [TEL-1945] recarrega o relatório que está na tela — a barra de filtros é compartilhada
    // pelos dois, então despachar sempre para /calls buscava o relatório errado na aba Usuários.
    // [TEL-1945] Sem os botoes "Aplicar/Limpar", cada clique num filtro vale uma busca
    // completa — e uma busca grande gasta varias requisicoes, porque o front pagina seguindo
    // meta.nextPage. Contra o limiter de 15/min por usuario do /calls, marcar tres ramais
    // seguidos ja custa tres buscas. O debounce agrupa a rajada numa so, sem tirar a sensacao
    // de "aplica na hora".
    scheduleRefresh () {
      clearTimeout(this._refreshTimer)
      this._refreshTimer = setTimeout(() => this.refreshCurrentReport(), 400)
    },
    refreshCurrentReport () {
      if (this.reportType === 'agents') {
        // O de Chamadas não é recarregado agora; fica marcado para recarregar ao voltar.
        this.callsReportStale = true
        this.usersCurrentPage = 1
        return this.loadUsersReport()
      }
      this.usersReportLoaded = false
      this.currentPage = 1
      // O filtro vale para os dois relatórios, então a paginação do de Usuários
      // também precisa voltar ao início: mantida na página antiga, a tabela mostra
      // "Nenhum registro encontrado" ao reabrir a aba, com os dados na página 1.
      this.usersCurrentPage = 1
      return this.getCdrs()
    },
    // [TEL-1945] carrega o relatório de Usuários via API (GET /reports/agents); substitui o antigo mock embutido
    async loadUsersReport () {
      // O gif da barra de filtros e compartilhado pelas duas abas, mas a flag nao:
      // desligar a de Chamadas aqui apagaria o indicador de uma paginacao em curso.
      this.loadingUsers = true
      // Guarda de geracao, como no getCdrs: e uma requisicao so, mas nada impede duas
      // trocas de filtro seguidas, e a resposta lenta da primeira sobrescreveria a segunda.
      const requestId = (this._agentsRequestId || 0) + 1
      this._agentsRequestId = requestId
      try {
        this.usersError = null
        const response = await loopback.get('/reports/agents', {
          params: this.agentsReportParams()
        })
        const rows =
          response && response.data
            ? response.data
            : Array.isArray(response)
              ? response
              : []
        if (requestId !== this._agentsRequestId) return
        this.userReportItems = Array.from(rows)
        this.usersReportLoaded = true
      } catch (e) {
        if (requestId !== this._agentsRequestId) return
        this.userReportItems = []
        // Não marca como carregado: reabrir a aba tenta de novo, em vez de mostrar
        // "Nenhum registro encontrado" para o que foi, na verdade, uma falha de rede.
        this.usersReportLoaded = false
        this.usersError =
          'Não foi possível carregar o relatório de usuários. Tente novamente.'
      } finally {
        // A busca superada nao apaga o spinner da que ainda esta rodando.
        if (requestId === this._agentsRequestId) this.loadingUsers = false
      }
    },
    // [TEL-1945] O dropdown lista TODOS os ramais do domínio, inclusive quem não tem chamada
    // no período. O requisito 13 do card pedia o contrário ("se o usuario nunca fez uma chamada,
    // não trazer o mesmo"), e a decisão de produto foi não implementar: uma lista que muda de
    // tamanho conforme o período filtrado é mais confusa do que uma que sempre mostra a equipe
    // inteira, e o custo seria uma agregação a mais por troca de filtro.
    // Fica registrado para o próximo leitor não tratar como esquecimento e "consertar".
    async loadExtensions () {
      try {
        const response = await loopback.get('/extensions')

        if (response && Array.isArray(response)) {
          this.extensions = response
        }
      } catch (error) {
        this.extensions = []
      }
    },
    // [TEL-1945] So escreve quando a selecao realmente mudou. O watcher de hangupCause dispara
    // por referencia, entao reatribuir um array de conteudo identico — o que acontece a cada
    // "Mostrar mais/menos opcoes" — custava uma busca paginada inteira sem nenhum filtro novo.
    setHangupCause (causes) {
      const current = this.advancedFilters.hangupCause || []
      const changed =
        current.length !== causes.length ||
        causes.some((cause, index) => cause !== current[index])
      if (changed) this.advancedFilters.hangupCause = causes
    },
    handleHangupCauseChange (newValue) {
      if (!newValue) return

      const hasShowMore = newValue.includes('__SHOW_MORE__')
      const hasShowLess = newValue.includes('__SHOW_LESS__')

      if (hasShowMore) {
        const cleanValue = newValue.filter(value => value !== '__SHOW_MORE__')
        this.setHangupCause(cleanValue)
        this.showAllHangupCauses = true
        return
      }

      if (hasShowLess) {
        const cleanValue = newValue.filter(value => value !== '__SHOW_LESS__')

        const mainCauses = [
          'NORMAL_CLEARING',
          'USER_BUSY',
          'UNALLOCATED_NUMBER',
          'NUMBER_CHANGED',
          'ORIGINATOR_CANCEL',
          'RECOVERY_ON_TIMER_EXPIRE'
        ]

        const textToMainCause = {}
        mainCauses.forEach(cause => {
          if (HANGUPCAUSES_MAPPER[cause]) {
            const text = HANGUPCAUSES_MAPPER[cause]
            textToMainCause[text] = cause
          }
        })

        const filteredCauses = []
        const usedTexts = new Set()

        cleanValue.forEach(selectedCause => {
          const selectedText = HANGUPCAUSES_MAPPER[selectedCause]
          if (selectedText && !usedTexts.has(selectedText)) {
            if (textToMainCause[selectedText]) {
              filteredCauses.push(textToMainCause[selectedText])
              usedTexts.add(selectedText)
            }
          }
        })

        this.setHangupCause(filteredCauses)
        this.showAllHangupCauses = false
        return
      }

      this.setHangupCause(newValue)
    }
  },
  computed: {
    // O gif da barra de filtros e compartilhado pelas duas abas.
    loading () {
      return this.loadingCalls || this.loadingUsers
    },
    // [TEL-1945] chips dos filtros avançados visíveis na aba atual. Fora 'from', que é
    // compartilhado, os demais filtros só existem no relatório de Chamadas — anunciar um chip
    // cujo dropdown está escondido na aba (e que nem se aplica ao relatório) só confunde.
    // Resolvido em computed também para não usar v-for + v-if no mesmo elemento.
    visibleFilterChips () {
      const active = this.getActiveFilters()
      return Object.keys(active)
        .filter(key => this.reportType === 'calls' || key === 'from')
        .filter(key => !(key === 'to' && this.isPhoneNumberHidden))
        .map(key => ({ key, display: this.getFilterDisplayValue(key, active[key]) }))
        .filter(chip => chip.display)
    },
    hasVisibleChips () {
      if (this.visibleFilterChips.length > 0) return true
      if (this.isDateFiltered) return true
      return this.reportType === 'agents' && this.usersStatusFilter.length > 0
    },
    // true quando o período foi editado (diferente do default "Hoje") — controla chip e botões
    isDateFiltered () {
      return this.dateFilterLabel !== 'Hoje'
    },
    // [TEL-1945] fonte da tabela de Usuários — populada por loadUsersReport() via GET /reports/agents
    userReportRows () {
      // [TEL-1945] o filtro de Status é aplicado pelo backend (TEL-2164) — filtrar aqui
      // recortaria só a página carregada e divergiria do que a exportação devolve.
      return this.userReportItems
    },
    // [TEL-1945] rótulo do dropdown Status (Usuários)
    usersStatusLabel () {
      const n = this.usersStatusFilter.length
      return n === 0 ? 'Status' : `${n} selecionado(s)`
    },
    // rótulo do dropdown Ramais (multi)
    extensionsLabel () {
      // [TEL-1945] rótulo "Ramais" -> "Usuários" (filtra por nome ou ramal)
      if (!this.advancedFilters.from || this.advancedFilters.from.length === 0) { return 'Usuários' }
      return `${this.advancedFilters.from.length} selecionado(s)`
    },
    // rótulo do dropdown Causas do desligamento (multi)
    hangupCauseLabel () {
      const list = this.advancedFilters.hangupCause
      if (!list || list.length === 0) return 'Motivo do desligamento'
      return `${list.length} selecionada(s)`
    },
    // rótulo do dropdown Número discado
    numberLabel () {
      return this.advancedFilters.to
        ? `Nº: ${this.advancedFilters.to}`
        : 'Número discado'
    },
    // rótulo dinâmico do dropdown Tipo de chamada (binding callType inalterado)
    callTypeLabel () {
      const map = {
        '': 'Tipo de chamada',
        outbound: 'Tipo: Sainte',
        inbound: 'Tipo: Entrante'
      }
      return map[this.advancedFilters.callType] || 'Tipo de chamada'
    },
    // rótulo dinâmico do dropdown Duração (binding durationRange inalterado)
    durationLabel () {
      return this.formatDurationRange(this.advancedFilters.durationRange)
    },
    // [TEL-1945] plano ilimitado não tem tarifa nem custo por ligação
    isUnlimited () {
      const org = this.$store.state.auth && this.$store.state.auth.organization
      return !!(org && org.isUnlimited === true)
    },
    // número de contato oculto para quem vê: marca na organização OU no usuário
    // (GET /accounts e GET /users/me). Esconde o filtro Número discado.
    isPhoneNumberHidden () {
      const auth = this.$store.state.auth
      const org = auth && auth.organization
      const user = auth && auth.user
      return !!(
        (org && org.hidePhoneNumber === true) ||
        (user && user.hidePhoneNumber === true)
      )
    },
    // [TEL-1945] mesmo padrão de MenuHeader.vue / Home.vue
    isAdmin () {
      const user = this.$store.state.auth && this.$store.state.auth.user
      return !!(user && user.role === 'ADMIN')
    },
    // [TEL-1945] mesmo padrão usado em Integration/ExactSales.vue
    hasAI () {
      const user = this.$store.state.auth && this.$store.state.auth.user
      return !!(user && user.hasAI === true)
    },
    // chaves de colunas oferecidas p/ o seletor de colunas
    availableColumnKeys () {
      return Object.keys(this.fields).filter(key => {
        // __cols__ é coluna de ações (engrenagem), não entra no seletor
        if (key === '__cols__') return false
        // [TEL-1945] em plano ilimitado as colunas de valor nem chegam ao seletor
        if (this.isUnlimited && PRICE_COLUMNS.includes(key)) return false
        // [TEL-1945] idem para as colunas de IA em conta sem o recurso
        if (!this.hasAI && AI_COLUMNS.includes(key)) return false
        // número oculto: Metadado nem entra no seletor (o getFilteredFields segue como guarda)
        if (this.isPhoneNumberHidden && key === 'metadata') return false
        return true
      })
    },
    // opções (value/text) do seletor de colunas, na ordem de fields
    columnOptions () {
      return this.availableColumnKeys.map(key => ({
        value: key,
        text: this.fields[key].label
      }))
    },
    // chaves de colunas do Usuários (exclui __cols__)
    usersAvailableColumnKeys () {
      return Object.keys(this.userReportFields).filter(
        key => key !== '__cols__'
      )
    },
    // opções do seletor de colunas do Usuários
    usersColumnOptions () {
      return this.usersAvailableColumnKeys.map(key => ({
        value: key,
        text: this.userReportFields[key].label
      }))
    },
    hangupCauseOptions () {
      return HANGUPCAUSES_MAPPER
    },
    hangupCauseMainOptionsArray () {
      const mainCauses = [
        'NORMAL_CLEARING',
        'USER_BUSY',
        'UNALLOCATED_NUMBER',
        'NUMBER_CHANGED',
        'ORIGINATOR_CANCEL',
        'RECOVERY_ON_TIMER_EXPIRE'
      ]

      const mainOptions = mainCauses
        .filter(cause => HANGUPCAUSES_MAPPER[cause])
        .map(cause => ({
          value: cause,
          text: HANGUPCAUSES_MAPPER[cause]
        }))

      mainOptions.push({
        value: '__SHOW_MORE__',
        text: 'Mostrar mais opções...'
      })

      return mainOptions
    },
    hangupCauseAllOptionsArray () {
      const uniqueOptions = []
      const usedTexts = new Set()

      Object.entries(HANGUPCAUSES_MAPPER).forEach(([value, text]) => {
        if (!usedTexts.has(text)) {
          usedTexts.add(text)
          uniqueOptions.push({
            value,
            text
          })
        }
      })

      uniqueOptions.push({
        value: '__SHOW_LESS__',
        text: 'Mostrar menos opções...'
      })

      return uniqueOptions
    },
    extensionOptionsArray () {
      if (!this.extensions || this.extensions.length === 0) {
        return []
      }

      const extensionsSorted = this.extensions
        .slice()
        .sort((a, b) => a.ramal.localeCompare(b.ramal))
        .map(extension => ({
          // value continua o ramal (query igual); text mostra "Nome (ramal)"
          value: extension.ramal,
          text: `${`${extension.first_name || ''} ${extension.last_name ||
            ''}`.trim() || extension.ramal} (${extension.ramal})`
        }))

      return extensionsSorted
    },
    // [TEL-1945] opções de Ramais filtradas pela busca (nome ou número)
    filteredExtensionOptions () {
      const term = (this.extensionSearch || '').trim().toLowerCase()
      if (!term) return this.extensionOptionsArray
      return this.extensionOptionsArray.filter(
        opt =>
          String(opt.text).toLowerCase().indexOf(term) !== -1 ||
          String(opt.value).toLowerCase().indexOf(term) !== -1
      )
    }
  },
  watch: {
    startedAt: {
      handler: function startedAtObserver (to, from) {
        if (
          to &&
          from &&
          to.toJSON().slice(0, 10) !== from.toJSON().slice(0, 10) &&
          !this.loading
        ) { this.scheduleRefresh() }
      },
      immediate: true
    },
    endedAt: {
      handler: function endedAtObserver (to, from) {
        if (
          to &&
          from &&
          to.toJSON().slice(0, 10) !== from.toJSON().slice(0, 10) &&
          !this.loading
        ) { this.scheduleRefresh() }
      },
      immediate: true
    },
    // [TEL-1945] carrega o relatório de Usuários on-demand na 1ª vez que abrir a aba Usuários
    reportType (to) {
      if (to === 'agents') {
        if (!this.usersReportLoaded) this.loadUsersReport()
        return
      }
      if (this.callsReportStale) this.getCdrs()
    },
    // [TEL-1945] mantém o campo do dropdown em sincronia com o filtro aplicado: sem isto,
    // reabrir "Número discado" mostraria o campo vazio com o chip aceso.
    'advancedFilters.to' (to) {
      if (to !== this.pendingNumber) this.pendingNumber = to || ''
    },
    // [TEL-1945] Status é filtro do relatório de Usuários e agora vai ao backend: sem este
    // watcher o filtro deixaria de ter efeito ao sair do client-side.
    usersStatusFilter () {
      if (this.reportType !== 'agents') return
      this.scheduleRefresh()
    },
    // [TEL-1945] filtros de seleção aplicam na hora, no relatório que está aberto
    'advancedFilters.from' () {
      this.scheduleRefresh()
    },
    'advancedFilters.callType' () {
      this.scheduleRefresh()
    },
    'advancedFilters.hangupCause' () {
      this.scheduleRefresh()
    },
    'advancedFilters.durationRange' () {
      this.scheduleRefresh()
    }
  }
}
</script>

<style lang="scss" scoped>
.form-control {
  height: 15px;
}

.date {
  max-width: 120px;
  background-color: white;
}

.filter-buttons-container {
  .btn {
    height: 35px;
  }
}
.cursor {
  cursor: pointer;
}

.download-excel {
  height: 35px;
}

.advanced-filter-container {
  width: 100%;
  position: relative;

  .filter-button-position {
    float: right;
  }

  .btn-outline-secondary {
    &:hover {
      .ik-filter {
        color: #6c757d !important;
      }
    }

    .ik-filter.text-primary {
      color: #007bff !important;
    }
  }
}

.per-page-selector {
  width: 80px !important;
  height: 2rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  color: #374151;
  font-size: 0.8125rem;
}

.active-filters-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background-color: #eff6ff;
    color: #2563eb;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    border: 1px solid #bfdbfe;

    .ik-x {
      cursor: pointer;
      font-size: 0.75rem;

      &:hover {
        color: #d32f2f;
      }
    }
  }
}

/* Design system Api4Com — escopo .report-ds (espelha .dashboard-ds) */
.report-ds {
  font-family: inherit; /* herda a fonte padrão do app (Inter removida) */

  .card {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  }

  .btn {
    border-radius: 0.375rem;
    font-weight: 500;
  }

  .btn-primary {
    background-color: #2563eb;
    border-color: #2563eb;

    &:hover,
    &:focus {
      background-color: #1d4ed8;
      border-color: #1d4ed8;
    }
  }

  .btn-outline-secondary {
    color: #475569;
    border-color: #e2e8f0;
    background-color: #ffffff;

    &:hover,
    &:focus {
      background-color: #f8fafc;
      border-color: #cbd5e1;
      color: #0f172a;
    }
  }

  .dropdown-menu {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    padding: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .dropdown-item {
    color: #374151;
    border-radius: 0.5rem;
    font-size: 0.875rem;

    &:hover,
    &:focus {
      background-color: #f3f4f6;
      color: #1f2937;
    }
  }
}

/* Área da tabela rola na horizontal em telas estreitas (em vez de cortar/espremer) */
.report-ds .card-table {
  overflow-x: auto;
  width: 100%;
}

/* Tabela limpa (espelha .vendor-table): sem zebra, header suave, linhas arejadas — usa /deep/ pois a b-table gera thead/td internamente */
.report-ds /deep/ .table {
  /* separate p/ permitir cantos arredondados no cabeçalho (só apresentação) */
  border-collapse: separate;
  border-spacing: 0;

  thead th {
    background: #f3f4f6;
    color: #6b7280;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    text-align: center; /* título centralizado */
    border: none;
    border-bottom: 1px solid #e5e7eb;
    padding: 1rem;
    vertical-align: middle;
    white-space: nowrap;
  }

  /* cantos arredondados no topo do cabeçalho */
  thead th:first-child {
    border-top-left-radius: 0.5rem;
  }
  thead th:last-child {
    border-top-right-radius: 0.5rem;
  }

  /* ordenação: seta DEPOIS do texto (à direita), inline — substitui o ícone default (background) da b-table */
  thead th[aria-sort] {
    cursor: pointer;
    background-image: none !important;
    padding-right: 1rem !important;
  }
  /* ícone igual ao Dashboard (UserPerformanceTable): seta-dupla esmaecida sempre + triângulo de direção quando ordenado */
  thead th[aria-sort]::after {
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' opacity='0.55'%3E%3Cpath d='m21 16-4 4-4-4'/%3E%3Cpath d='M17 20V4'/%3E%3Cpath d='m3 8 4-4 4 4'/%3E%3Cpath d='M7 4v16'/%3E%3C/svg%3E");
    display: inline-block;
    width: 12px;
    text-align: center;
    margin-left: 0.35rem;
    vertical-align: middle;
    color: #94a3b8;
    font-size: 10px;
    line-height: 1;
  }
  /* ordenada: mostra SÓ o triângulo (esconde a seta-dupla), mesma largura -> sem deslocamento */
  thead th[aria-sort='ascending']::after {
    content: '▲';
  }
  thead th[aria-sort='descending']::after {
    content: '▼';
  }

  tbody td {
    background: #ffffff;
    border: none;
    border-top: 1px solid #e5e7eb;
    padding: 0.75rem 1rem;
    vertical-align: middle;
    text-align: center; /* dados centralizados (igual ao título da coluna) */
    color: #1f2937;
    font-size: 0.875rem;
    font-weight: 400;
  }

  /* evita linha dupla header/1ª linha (separate): o border-bottom do th já separa */
  tbody tr:first-child td {
    border-top: none;
  }

  /* hover de linha */
  tbody tr:hover td {
    background-color: #f9fafb;
  }
}

/* Painel de Filtros Avançados no padrão do design system (só visual) */
.report-ds .report-advanced-filter {
  .card-header {
    background-color: #f5f7fa !important;
    border-bottom: 1px solid #eef2f6;

    h5 {
      color: #0f172a;
      font-size: 1rem;
      font-weight: 600;
    }
  }

  .form-label {
    color: #64748b;
    font-weight: 500;
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }

  .form-control {
    height: auto;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    color: #0f172a;

    &:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
    }
  }
}

/* Botão quadrado de engrenagem + popover de colunas (não corta, não fecha ao marcar) */
.report-ds .column-gear-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .ik {
    font-size: 16px;
    line-height: 1;
  }
}

/* Engrenagem de colunas com ícone neutro (via /deep/ p/ alcançar o toggle do b-dropdown) */
.report-ds /deep/ .column-gear-btn {
  /* fundo cinza mais escuro p/ deixar claro que é clicável (destaca do header) */
  background-color: #d1d5db;
  border: 1px solid #c4c9d0;
  color: #374151;
}
.report-ds /deep/ .column-gear-btn:hover,
.report-ds /deep/ .column-gear-btn:focus {
  background-color: #9ca3af;
  border-color: #9ca3af;
  color: #1f2937;
}

/* coluna de ações estreita; header alinha a engrenagem à direita (logo após "Gravação") */
.report-ds /deep/ .cols-gear-th {
  /* 1% + nowrap: a coluna encolhe ao conteúdo (engrenagem no header, botão Copiar no corpo) */
  width: 1%;
  text-align: right;
  white-space: nowrap;
}
.report-ds /deep/ .cols-gear-td {
  width: 1%;
  text-align: right;
  white-space: nowrap;
}

.report-ds /deep/ .column-picker-menu {
  /* width auto sobrescreve o global .dropdown-menu { width:170px }; min-width maior p/ caber rótulos longos numa linha */
  width: auto;
  min-width: 260px;
  max-height: 320px;
  overflow-y: auto;
  padding: 0.25rem 0;
  /* itens à esquerda (o th é centralizado; aqui não deve herdar) */
  text-align: left;

  .b-dropdown-form {
    padding: 0.25rem 1rem;
    text-align: left;
  }

  .custom-control {
    text-align: left;
  }

  .custom-control-label {
    /* nunca quebrar em 2 linhas — checkbox à esquerda, texto seguindo numa linha só */
    white-space: nowrap;
    line-height: 1.3;
  }
}

/* Check redondo azul no popover de colunas (igual aos filtros) */
.report-ds /deep/ .column-picker-menu .custom-control-label::before {
  border-radius: 50%;
  border: 2px solid #2563eb;
  background-color: transparent;
}
.report-ds
  /deep/
  .column-picker-menu
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #2563eb;
  border-color: #2563eb;
}

/* Cabeçalho de página (espelha .dashboard-page-header) */
.report-ds .report-page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}
.report-ds .report-page-header__icon {
  padding: 0.5rem;
  border-radius: 0.5rem;
  background: #374151;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.report-ds .report-page-header__icon svg {
  width: 20px;
  height: 20px;
}
.report-ds .report-page-header__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
  margin: 0;
}
/* subtítulo de explicação (espelha .dashboard-page-header__subtitle) */
.report-ds .report-page-header__subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
  margin: 4px 0 0 0;
}
/* rótulo "Tipo de relatório:" no molde do "Filtros:" do dashboard */
.report-ds .report-type-label {
  color: #6b7280;
  white-space: nowrap;
}
/* Card-header limpo (fundo branco + divisória) */
.report-ds .card-header {
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}
/* [TEL-1945] Seletor de tipo: segmented control (pílula com destaque azul deslizante) */
.report-ds .report-type-segmented {
  position: relative;
  display: inline-flex;
  padding: 3px;
  background-color: #f3f4f6;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
}
/* destaque azul que desliza entre as opções */
.report-ds .report-type-segmented__thumb {
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc(50% - 3px);
  background-color: #2563eb;
  border-radius: 9999px;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.35);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
}
.report-ds .report-type-segmented--agents .report-type-segmented__thumb {
  transform: translateX(100%);
}
.report-ds .report-type-segmented__opt {
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  min-width: 96px;
  border: none;
  background: transparent;
  padding: 0.35rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  border-radius: 9999px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.25s ease;
}
.report-ds .report-type-segmented__opt.active {
  color: #ffffff;
}
.report-ds .report-type-segmented__opt:focus {
  outline: none;
}

.report-ds .agents-placeholder {
  border: 1px dashed #e2e8f0;
  box-shadow: none;
}

/* Barra de filtros perfilada (padrão do dashboard) */
.report-ds .report-filter-bar {
  margin-bottom: 1rem;
}
.report-ds .report-filter-bar__label {
  color: #6b7280;
  white-space: nowrap;
}

/* Pills dos filtros: geometria + estado neutro (visual novo) */
.report-ds .report-filter-dropdown /deep/ .btn {
  height: 2.25rem;
  border-radius: 0.375rem;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  cursor: pointer;
}
.report-ds .report-filter-dropdown /deep/ .btn-outline-secondary {
  background-color: #f9fafb;
  border-color: #e5e7eb;
  color: #374151;
}
.report-ds .report-filter-dropdown /deep/ .btn-outline-secondary:hover,
.report-ds .report-filter-dropdown /deep/ .btn-outline-secondary:focus {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

/* Pills dos filtros ATIVAS (azul outline) — o ativo usa variant=primary */
.report-ds .report-filter-dropdown /deep/ .btn-primary {
  background-color: #eff6ff;
  border-color: #2563eb;
  color: #2563eb;
}
/* [TEL-1945] hover/focus/active mantêm o texto AZUL (bootstrap forçava branco) */
.report-ds .report-filter-dropdown /deep/ .btn-primary:hover,
.report-ds .report-filter-dropdown /deep/ .btn-primary:focus,
.report-ds .report-filter-dropdown /deep/ .btn-primary:active,
.report-ds .report-filter-dropdown /deep/ .btn-primary.active,
.report-ds
  .report-filter-dropdown
  /deep/
  .btn-primary:not(:disabled):not(.disabled):active,
.report-ds .report-filter-dropdown.show /deep/ .btn-primary.dropdown-toggle {
  background-color: #dbeafe;
  border-color: #2563eb;
  color: #2563eb !important;
  box-shadow: none;
}

/* [TEL-1945] ícone de informação (i) nos cabeçalhos (relatório de Usuários) */
.report-ds .report-info-icon {
  margin-left: 4px;
  color: #9ca3af;
  cursor: help;
  vertical-align: middle;
  outline: none;
}
.report-ds .report-info-icon:hover,
.report-ds .report-info-icon:focus {
  color: #6b7280;
}

/* Período personalizado (espelha .dashboard-ds .period-*) */
.report-ds .period-menu {
  min-width: 240px;
}
.report-ds .period-custom-form {
  padding: 0;
}
.report-ds .period-custom-toggle {
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
}
.report-ds .period-custom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 1.5rem 12px;
}
.report-ds .period-custom-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  color: #64748b;
}
.report-ds .period-custom input[type="date"] {
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
}
.report-ds .period-custom-error {
  font-size: 12px;
  color: #dc2626;
}
.report-ds .period-custom .btn-primary {
  align-self: flex-start;
}

/* [TEL-1945] Duração personalizada (molde do período) + operador segmentado menor/maior que */
.report-ds .duration-menu {
  min-width: 240px;
}
.report-ds .duration-custom-form {
  padding: 0;
}
.report-ds .duration-custom-toggle {
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
}
.report-ds .duration-custom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 1.5rem 12px;
}
.report-ds .duration-op-toggle {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}
.report-ds .duration-op-btn {
  flex: 1 1 0;
  padding: 6px 8px;
  font-size: 12px;
  background: #ffffff;
  border: none;
  color: #64748b;
  cursor: pointer;
}
.report-ds .duration-op-btn + .duration-op-btn {
  border-left: 1px solid #e2e8f0;
}
.report-ds .duration-op-btn.active {
  background: #2563eb;
  color: #ffffff;
  font-weight: 600;
}
.report-ds .duration-custom-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  color: #64748b;
}
.report-ds .duration-custom input[type="number"] {
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
}
.report-ds .duration-custom-error {
  font-size: 12px;
  color: #dc2626;
}
.report-ds .duration-custom .btn-primary {
  align-self: flex-start;
}

/* Menu de checkboxes dos filtros (Ramais/Causas) — labels quebram linha + rolagem se a lista for longa */
/* /deep/ p/ alcançar o .dropdown-menu gerado pela b-dropdown; min-width contém o form (min-width 220) e a busca DENTRO do box, sem vazar */
.report-ds /deep/ .report-checkbox-menu {
  min-width: 240px;
  max-height: 280px;
  overflow-x: hidden;
  overflow-y: auto;
}
/* [TEL-1945] busca de Ramais: dentro do box, fixa no topo enquanto a lista rola */
.report-ds /deep/ .report-checkbox-menu .report-ext-search {
  position: sticky;
  top: 0;
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  background-color: #ffffff;
}
.report-ds .report-checkbox-menu .custom-control-label {
  white-space: normal;
  line-height: 1.3;
}
/* Check redondo azul nos menus de checkbox (Ramais/Causas) — só visual, lógica v-model intacta */
.report-ds /deep/ .report-checkbox-menu .custom-control-label::before {
  border-radius: 50%;
  border: 2px solid #2563eb;
  background-color: transparent;
}
.report-ds
  /deep/
  .report-checkbox-menu
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #2563eb;
  border-color: #2563eb;
}

/* Mini-form do filtro Número discado (molde do "Personalizado") */
.report-ds .report-number-menu {
  min-width: 240px;
}
.report-ds .number-custom-form {
  padding: 0;
}
.report-ds .number-custom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 1.5rem 12px;
}
.report-ds .number-custom-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  color: #64748b;
}
.report-ds .number-custom input[type="text"] {
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
}
.report-ds .number-custom .btn-primary {
  align-self: flex-start;
}

/* Aplicar/Limpar: altura alinhada às pills; Limpar neutro */

/* Botão de download como pill neutra (ícone PNG mantido) */
.report-ds .report-download-btn {
  height: 2.25rem;
  background-color: #f9fafb;
  border-color: #e5e7eb;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}
.report-ds .report-download-btn:hover,
.report-ds .report-download-btn:focus {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

/* Paginação: cor ativa no azul do design */
.report-ds /deep/ .pagination .page-link {
  color: #2563eb;
}
.report-ds /deep/ .pagination .page-item.active .page-link {
  background-color: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

/* Botão Aplicar filtros: azul (salvar) — mesmo tratamento do filtro ativo */
/* Botão Limpar: fundo vermelho claro + borda vermelha (estilo do filtro ativo, em vermelho) */

/* [TEL-1945] Badge da coluna Status (relatório de Usuários) */
.report-ds .report-status-badge {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
}
.report-ds .report-status-badge--ativo {
  background-color: #dcfce7;
  color: #166534;
}
.report-ds .report-status-badge--pendente {
  background-color: #fef9c3;
  color: #854d0e;
}
.report-ds .report-status-badge--inativo {
  background-color: #f3f4f6;
  color: #6b7280;
}

/* [TEL-1945] Resumo/Transcrição: ícone de IA na célula + painel expandido (row-details) */
.report-ds .report-summary-icon {
  color: #7c3aed;
}
.report-ds .report-summary-icon:hover {
  color: #5b21b6;
}
.report-ds .report-summary-details {
  background-color: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 0.25rem 0.5rem;
  text-align: left;
}
.report-ds .report-summary-details__header {
  display: flex;
  align-items: center;
  color: #6d28d9;
  margin-bottom: 0.5rem;
}
/* [TEL-1945] trava o tamanho do ícone no cabeçalho do painel (evita SVG estourar sem width/height) */
.report-ds .report-summary-details__header svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.report-ds .report-summary-details__header .close {
  font-size: 1.1rem;
  line-height: 1;
  margin-left: auto;
}
.report-ds .report-summary-details__text {
  color: #374151;
  white-space: pre-line;
}

/* [TEL-2256] texto de IA renderizado a partir de Markdown pelo markdown-it. O `pre-line` acima
   existe para o texto cru (estados de carregando/erro/sem conteúdo); aqui as quebras já viraram
   tags, então mantê-lo dobraria o espaçamento entre blocos. Os seletores precisam de /deep/ porque
   o conteúdo vem de v-html e não recebe o atributo de escopo do componente. */
.report-ds .report-summary-details__text--md {
  white-space: normal;
  line-height: 1.55;
}
.report-ds .report-summary-details__text--md /deep/ p {
  margin: 0 0 0.6rem;
}
/* markdown-it emite o nível real do título (## -> h2), e h1/h2 do Bootstrap passam de 2rem — o
   painel já tem o próprio cabeçalho, então toda a escala é reduzida e a hierarquia fica por
   tamanho e cor */
.report-ds .report-summary-details__text--md /deep/ h1,
.report-ds .report-summary-details__text--md /deep/ h2,
.report-ds .report-summary-details__text--md /deep/ h3,
.report-ds .report-summary-details__text--md /deep/ h4,
.report-ds .report-summary-details__text--md /deep/ h5,
.report-ds .report-summary-details__text--md /deep/ h6 {
  color: #4c1d95;
  font-weight: 600;
  margin: 1rem 0 0.4rem;
}
.report-ds .report-summary-details__text--md /deep/ h1,
.report-ds .report-summary-details__text--md /deep/ h2 {
  font-size: 1.05rem;
}
.report-ds .report-summary-details__text--md /deep/ h3,
.report-ds .report-summary-details__text--md /deep/ h4 {
  font-size: 0.95rem;
}
.report-ds .report-summary-details__text--md /deep/ h5,
.report-ds .report-summary-details__text--md /deep/ h6 {
  font-size: 0.875rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
/* zera a folga das pontas: o painel já tem padding próprio */
.report-ds .report-summary-details__text--md /deep/ h1:first-child,
.report-ds .report-summary-details__text--md /deep/ h2:first-child,
.report-ds .report-summary-details__text--md /deep/ h3:first-child,
.report-ds .report-summary-details__text--md /deep/ h4:first-child,
.report-ds .report-summary-details__text--md /deep/ h5:first-child,
.report-ds .report-summary-details__text--md /deep/ h6:first-child {
  margin-top: 0;
}
.report-ds .report-summary-details__text--md /deep/ p:last-child,
.report-ds .report-summary-details__text--md /deep/ ul:last-child,
.report-ds .report-summary-details__text--md /deep/ ol:last-child,
.report-ds .report-summary-details__text--md /deep/ table:last-child,
.report-ds .report-summary-details__text--md /deep/ blockquote:last-child {
  margin-bottom: 0;
}
.report-ds .report-summary-details__text--md /deep/ ul,
.report-ds .report-summary-details__text--md /deep/ ol {
  margin: 0 0 0.6rem;
  padding-left: 1.35rem;
}
.report-ds .report-summary-details__text--md /deep/ li {
  margin-bottom: 0.2rem;
}
/* lista "solta" (itens separados por linha em branco no texto da IA): o markdown-it embrulha cada
   item num <p>, que traria a margem cheia para dentro do <li> */
.report-ds .report-summary-details__text--md /deep/ li > p {
  margin-bottom: 0;
}
.report-ds .report-summary-details__text--md /deep/ li > p + p {
  margin-top: 0.4rem;
}
.report-ds .report-summary-details__text--md /deep/ li:not(:last-child) > p:only-child {
  margin-bottom: 0.4rem;
}
.report-ds .report-summary-details__text--md /deep/ strong {
  color: #1f2937;
  font-weight: 600;
}
.report-ds .report-summary-details__text--md /deep/ em {
  color: #4b5563;
}
.report-ds .report-summary-details__text--md /deep/ a {
  color: #6d28d9;
  text-decoration: underline;
}
.report-ds .report-summary-details__text--md /deep/ code {
  background-color: rgba(109, 40, 217, 0.08);
  border-radius: 0.25rem;
  color: #5b21b6;
  font-size: 0.85em;
  padding: 0.1rem 0.3rem;
}
.report-ds .report-summary-details__text--md /deep/ pre {
  background-color: rgba(109, 40, 217, 0.06);
  border-radius: 0.35rem;
  overflow-x: auto;
  padding: 0.6rem 0.75rem;
}
.report-ds .report-summary-details__text--md /deep/ blockquote {
  border-left: 3px solid #d8b4fe;
  color: #4b5563;
  margin: 0 0 0.6rem;
  padding-left: 0.75rem;
}
/* markdown-it renderiza tabelas; a do painel não pode estourar a largura da linha */
.report-ds .report-summary-details__text--md /deep/ table {
  border-collapse: collapse;
  display: block;
  margin-bottom: 0.6rem;
  overflow-x: auto;
  width: 100%;
}
.report-ds .report-summary-details__text--md /deep/ th,
.report-ds .report-summary-details__text--md /deep/ td {
  border: 1px solid #e9d5ff;
  padding: 0.3rem 0.5rem;
  text-align: left;
}
.report-ds .report-summary-details__text--md /deep/ hr {
  border: 0;
  border-top: 1px solid #e9d5ff;
  margin: 0.75rem 0;
}

/* [TEL-2146] estados do painel de IA: carregando / sem conteúdo / falha do upstream */
.report-ds .report-summary-details__text--muted {
  color: #6b7280;
  font-style: italic;
}

.report-ds .report-summary-details__text--error {
  color: #b91c1c;
}

/* [TEL-1945] Avaliação da chamada: ícone estrela (âmbar) + acento no painel expandido */
.report-ds .report-eval-icon {
  color: #f59e0b;
}
.report-ds .report-eval-icon:hover {
  color: #b45309;
}
.report-ds .report-eval-details {
  background-color: #fffbeb;
  border-color: #fde68a;
}
.report-ds .report-eval-details .report-summary-details__header {
  color: #b45309;
}
/* [TEL-2256] o Markdown da análise qualitativa acompanha o âmbar do próprio painel */
.report-ds .report-eval-details .report-summary-details__text--md /deep/ h3,
.report-ds .report-eval-details .report-summary-details__text--md /deep/ h4,
.report-ds .report-eval-details .report-summary-details__text--md /deep/ h5,
.report-ds .report-eval-details .report-summary-details__text--md /deep/ h6 {
  color: #92400e;
}
.report-ds .report-eval-details .report-summary-details__text--md /deep/ code {
  background-color: rgba(180, 83, 9, 0.08);
  color: #92400e;
}
.report-ds .report-eval-details .report-summary-details__text--md /deep/ hr {
  border-top-color: #fde68a;
}
.report-ds .report-eval-details .report-summary-details__text--md /deep/ a {
  color: #b45309;
}
.report-ds .report-eval-details .report-summary-details__text--md /deep/ blockquote {
  border-left-color: #fcd34d;
}
.report-ds .report-eval-details .report-summary-details__text--md /deep/ pre {
  background-color: rgba(180, 83, 9, 0.06);
}
.report-ds .report-eval-details .report-summary-details__text--md /deep/ th,
.report-ds .report-eval-details .report-summary-details__text--md /deep/ td {
  border-color: #fde68a;
}

/* [TEL-1945] Colunas de ação (Resumo/Análise/Metadado/Gravação): botão com fundo, borda e texto ao lado do ícone */
.report-ds .report-cell-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
}
.report-ds .report-cell-btn:hover,
.report-ds .report-cell-btn:focus {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  color: #1f2937;
  outline: none;
}
.report-ds .report-cell-btn svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}
/* acentos de cor por ícone */
.report-ds .report-cell-btn--ai svg {
  color: #7c3aed;
}
.report-ds .report-cell-btn--eval svg {
  color: #0ea5e9;
}
/* variante só ícone (Copiar): quadrado, sem gap de texto; o rótulo fica no title/aria-label */
.report-ds .report-cell-btn--icon {
  width: 28px;
  height: 28px;
  padding: 0;
  justify-content: center;
  gap: 0;
}
.report-ds .report-cell-btn--copy svg {
  color: #6b7280;
}
/* estado "Copiado": verde discreto por ~1,5s após o clique */
.report-ds .report-cell-btn--copied,
.report-ds .report-cell-btn--copied:hover,
.report-ds .report-cell-btn--copied:focus {
  background-color: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}
.report-ds .report-cell-btn--copied svg {
  color: #059669;
}
/* [TEL-2327] IA4COM: o ciano da faísca do menu; lado a lado com o Copiar */
.report-ds .report-cell-btn--ia4com {
  margin-right: 6px;
}
.report-ds .report-cell-btn--ia4com svg {
  color: #36bbef;
}
.report-ds .report-cell-btn--ia4com-open,
.report-ds .report-cell-btn--ia4com-open:hover,
.report-ds .report-cell-btn--ia4com-open:focus {
  background-color: #e0f5fd;
  border-color: #36bbef;
}
.report-ds /deep/ .report-row--ia4com > td {
  background-color: #f0faff;
}

</style>



// WEBPACK FOOTER //
// src/components/Report/Calls.vue